import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    Tooltip,
    Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Snackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material/styles';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import _ from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ColorConstants from '../../defaults/ColorConstants';
import DetailedFundingSources from '../../defaults/DetailedFundingSources.json';
import Events from '../../defaults/Events';
import FieldNames from '../../defaults/FieldNames.json';
import GridDefinitions from '../../defaults/GridDefinitions.json';
import {
    genericActivityValidationFieldSetFalse,
    genericActivityValidationFieldSetTrue,
    resetActivityValidationState,
} from '../../redux/slices/activityValidationSlice';
import { setFilterGeometry, setIsLocationFilterOnly } from '../../redux/slices/filterSlice';
import {
    genericTreatmentValidationFieldSetFalse,
    genericTreatmentValidationFieldSetTrue,
    resetTreatmentValidationState,
} from '../../redux/slices/treatmentValidationSlice';
import AuditLogService from '../../services/AuditLogService';
import CancelService from '../../services/CancelService';
import ErrorService from '../../services/ErrorService';
import FeatureService from '../../services/FeatureService';
import GeometryService from '../../services/GeometryService';
import GroupingService from '../../services/GroupingService';
import IDService from '../../services/IDService';
import LookupService from '../../services/LookupService';
import PlannedActualsService from '../../services/PlannedActualsService';
import UserHelperService from '../../services/UserHelperService';
import { getLogForFieldUpdatesOnExistingFeature } from '../../utils/AuditLogHelper';
import { getByLevel } from '../../utils/CategoryAssociationHelper';
import {
    calculateFiscalYearQuarter,
    formatDateForDisplay,
    getSaveableDate,
    getUTCMoment,
} from '../../utils/DateHelper';
import {
    addMeasurementEntry,
    getAllMissingFields,
    getAllMissingFieldsCount,
    getDepartureOptions,
    getFormGroupings,
    getRequiredFields,
    getStatus,
    isFeaturePostFire,
} from '../../utils/FeatureHelper';
import { removeCommas } from '../../utils/FormatHelper';
import InFormAlert from '../InFormAlert';
import InFormGenericModal from '../InFormGenericModal';
import '../list/TreatmentActivityList/TreatmentActivityList.css';
import './DetailsContainer.css';
import DetailsEdited from './DetailsEdited';
import DetailsHeader from './DetailsHeader';
import DetailsRequired from './DetailsRequired';
import fieldsDefinition from './FieldInfo.json';
import FormGroupings from './FormGroupings.json';
import AssociateChips from './customPanels/AssociateChips';
import BasicTextField from './customPanels/BasicTextField';
import CostsFundingPopover from './customPanels/CostsFundingPopover';
import DetailsGridItem from './customPanels/DetailsGridItem';
import { validateUnapprovalDate } from '../../utils/ValidationHelper';
import CancelEntryPopup from '../popover/CancelEntryPopup/CancelEntryPopup';
import InteragencyService from '../../services/InteragencyService';
import ProjectService from '../../services/ProjectService';
import * as esriLang from '@arcgis/core/core/lang.js';
import ProgressiveReportingList from './ProgressiveReportingList/ProgressiveReportingList';
import { setShowGlobalLoader, setStatusOptions } from '@store/slices/globalContextSlice';

const theme = createTheme({
    overrides: {
        MuiSnackbar: {
            root: {
                top: '94px !important',
            },
        },
    },
});

const useStyles = makeStyles(() => ({
    snackBarStyle: {
        top: '120px !important',
    },
}));

const drawerWidth = 650;

const openedMixin = (theme, headerheight) => ({
    width: drawerWidth,
    top: headerheight + 'px',
    left: 'auto',
    backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    overflowY: 'hidden',
});

const closedMixin = (theme, headerheight) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    top: headerheight + 'px',
    left: 'auto',
    backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const useNotSavedStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid gold',
        },
        '&&:after': {
            borderBottom: '2px solid gold',
        },
    },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open, headerheight }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme, headerheight),
            '& .MuiDrawer-paper': openedMixin(theme, headerheight),
        }),
        ...(!open && {
            ...closedMixin(theme, headerheight),
            '& .MuiDrawer-paper': closedMixin(theme, headerheight),
        }),
    })
);

//TODO: Break out Snackbar into own control, make more obvious

export default function DetailsContainer({
    isTreatment,
    feature,
    setFeature,
    mode,
    setMode,
    isNewFeature,
    setIsNewFeature,
    setIsApproved,
    isApproved,
    setIsTreatment,
    setIsCreatingActualFeature,
    setIsCreatingPlannedFeature,
    setPlannedFeature,
    setSelectedFeatures,
    userInfo,
    userRoleInfo,
    headerheight,
    rederiveFeatureInfo,
    estimatedActualConnection,
    project,
    setProject,
}) {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const statusOptions = useSelector((state) => state.globalContext.statusOptions);
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const config = useSelector((state) => state.configuration.config);
    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);

    const classes = useStyles();
    const notSavedClasses = useNotSavedStyles();

    const departureIndexOptions = getDepartureOptions();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [featureGroups, setFeatureGroups] = useState([]);
    const [groupDetails, setGroupDetails] = useState();
    // Action and related state constants
    const [baseTreatmentAttributes, setBaseTreatmentAttributes] = useState(null);
    const [baseActivityAttributes, setBaseActivityAttributes] = useState(null);
    const [editableFeature, setEditableFeature] = useState(null);
    const [expandedItems, setExpandedItems] = useState([]);
    const [classValue, setClassValue] = useState('');
    const [programValue, setProgramValue] = useState('');
    const [categoryValue, setCategoryValue] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [subTypeValue, setSubTypeValue] = useState('');
    const [associatedCount, setAssociatedCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasActual, setHasActual] = useState(null);
    const [disableEdit, setDisableEdit] = useState(false);
    const [disableEditMessage, setDisableEditMessage] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [actualsObject, setActualsObject] = useState(null);
    const [isApprovedAndFunded, setIsApprovedAndFunded] = useState(false);
    const [locationDepartmentValue, setLocationDepartmentValue] = useState('');
    const [locationAgencyValue, setLocationAgencyValue] = useState('');
    const [locationRegionValue, setLocationRegionValue] = useState('');
    const [locationUnitValue, setLocationUnitValue] = useState('');
    const [tribeNameValue, setTribeNameValue] = useState('');
    const [projectValue, setProjectValue] = useState(
        project?.attributes ? project.attributes[FieldNames.projectFields.name] : ''
    );
    const [unitOfMeasureValue, setUnitOfMeasureValue] = useState('');
    const [fundingSourceOptions, setFundingSourceOptions] = useState([]);
    const [contributedFundingSourceOptions, setContributedFundingSourceOptions] = useState([]);
    const [isCancelled, setIsCancelled] = useState(false);
    const [userAgencies, setUserAgencies] = useState([]);
    const [userRegions, setUserRegions] = useState([]);
    const [userUnits, setUserUnits] = useState([]);
    const [userSubUnits, setUserSubUnits] = useState([]);
    const [warningOpen, setWarningOpen] = useState(false);
    const [warningSeverity, setWarningSeverity] = useState('success');
    const [warningButtonText, setWarningButtonText] = useState('');
    const [warningTitle, setWarningTitle] = useState('');
    const [warningContent, setWarningContent] = useState('');
    const [dateFYWarningVal, setDateFYWarningVal] = useState('');
    const [requiredFields, setRequiredFields] = useState([]);
    const [featureHistoryRows, setFeatureHistoryRows] = useState([]);
    const [featureHistoryCols, setFeatureHistoryCols] = useState([]);
    const [costFundingPopoverOpen, setCostFundingPopoverOpen] = useState(false);
    const [isUnApprovalRequested, setIsUnApprovalRequested] = useState(false);
    const [unApprovalEditorOpen, setUnApprovalEditorOpen] = useState(false);
    const [unApprovalReason, setUnApprovalReason] = useState('');
    const [unApprovalDate, setUnApprovalDate] = useState(null);
    const [doClearCancelLogic, setDoClearCancelLogic] = useState(false);
    const [cancelEditorOpen, setCancelEditorOpen] = useState(false);
    const [interagencySelected, setInteragencySelected] = useState([]);
    const [otherInteragencySelected, setOtherInteragencySelected] = useState([]);
    const [itemsToDelete, setItemsToDelete] = useState([]);
    const [interagencyRefresh, setInteragencyRefresh] = useState(true);

    const costFields = FieldNames.costFields;

    const featureAuditLogGridColDefinitions = [
        GridDefinitions.UserAuditLog.modifiedBy,
        GridDefinitions.UserAuditLog.modifiedOn,
        GridDefinitions.UserAuditLog.modification,
    ];

    useCustomEventListener(Events.TriggerFieldChange, (data) => {
        if (data) {
            onHandleAttributeChanged(data.field, data.value);
        }
    });

    useCustomEventListener(Events.TriggerInteragencyFieldChange, (data) => {
        if (data) {
            const clone = [...otherInteragencySelected];
            clone?.map((item) => {
                if (item?.index == data?.index) {
                    item.InteragencyName = data?.value;
                    item.IsChanged = true;
                }
                return item;
            });
            setOtherInteragencySelected(clone);
        }
    });

    useCustomEventListener(Events.UpdateRequiredFields, (data) => {
        if (data) {
            let reqFields = [];
            reqFields = getRequiredFields(data.feature, data.isTreatment, null, userInfo, false);
            setRequiredFields(reqFields);
        }
    });

    useEffect(() => {
        const isProject = false;
        const isPostFire = isFeaturePostFire(feature?.attributes, isProject);
        if (
            interagencyRefresh &&
            feature?.attributes &&
            !isPostFire &&
            mode === FieldNames.modes.edit
        ) {
            InteragencyService.getAllInteragencyByJoinIDFeature(
                feature.attributes[FieldNames.featureFields.joinID],
                feature.attributes[FieldNames.featureFields.joinFeature]
            ).then((resp) => {
                const nonDefaultValues = resp?.filter(
                    (item) =>
                        item?.[FieldNames.interagencyFields.interagencyName] !==
                            FieldNames.interagencyValues.other &&
                        item?.[FieldNames.interagencyFields.interagencyName] !==
                            FieldNames.interagencyValues.nps &&
                        item?.[FieldNames.interagencyFields.interagencyName] !==
                            FieldNames.interagencyValues.fs &&
                        item?.[FieldNames.interagencyFields.interagencyName] !==
                            FieldNames.interagencyValues.bia &&
                        item?.[FieldNames.interagencyFields.interagencyName] !==
                            FieldNames.interagencyValues.blm &&
                        item?.[FieldNames.interagencyFields.interagencyName] !==
                            FieldNames.interagencyValues.fws
                );
                const defaultValues = resp?.filter(
                    (item) =>
                        item?.[FieldNames.interagencyFields.interagencyName] ===
                            FieldNames.interagencyValues.other ||
                        item?.[FieldNames.interagencyFields.interagencyName] ===
                            FieldNames.interagencyValues.nps ||
                        item?.[FieldNames.interagencyFields.interagencyName] ===
                            FieldNames.interagencyValues.fs ||
                        item?.[FieldNames.interagencyFields.interagencyName] ===
                            FieldNames.interagencyValues.bia ||
                        item?.[FieldNames.interagencyFields.interagencyName] ===
                            FieldNames.interagencyValues.blm ||
                        item?.[FieldNames.interagencyFields.interagencyName] ===
                            FieldNames.interagencyValues.fws
                );
                nonDefaultValues.map((item, i) => {
                    item.index = i;
                    item.IsChanged = false;
                    if (
                        item.InteragencyName.includes(FieldNames.interagencyValues.otherShortened)
                    ) {
                        item.InteragencyName = item.InteragencyName.split(
                            FieldNames.interagencyValues.otherShortened
                        )[1];
                    }
                });
                setInteragencyRefresh(false);
                setInteragencySelected(defaultValues);
                setOtherInteragencySelected(nonDefaultValues);
            });
        }
    }, [mode, interagencyRefresh, interagencySelected, feature]);

    useEffect(() => {
        if (feature) {
            const isActual = false;
            const tempFormGroupings = getFormGroupings(isTreatment, feature, isActual);
            setGroupDetails(tempFormGroupings);
        }
    }, [feature, isTreatment]);

    useEffect(() => {
        establishGridColumns();

        return () => {
            // Reset Data if needed on close of an Action/Feature
        };
    }, []);

    useEffect(() => {
        if (editableFeature) {
            // Requery when we change which Action/Feature we're looking at
            resetStateVariables();
        }
    }, [editableFeature?.attributes?.[FieldNames.featureFields.objectID]]);

    const resetStateVariables = () => {
        // Re-determine state variable values as needed upon significant change
        if (hasActual === null) {
            if (editableFeature.attributes[FieldNames.featureFields.joinID]) {
                PlannedActualsService.hasPlannedActualFeatures(
                    editableFeature.attributes[FieldNames.featureFields.joinFeature],
                    editableFeature.attributes[FieldNames.featureFields.joinID]
                )
                    .then((response) => {
                        if (
                            response?.attributes &&
                            (!response.attributes[FieldNames.featureFields.isArchived] ||
                                response.attributes[FieldNames.featureFields.isArchived] ===
                                    FieldNames.yesNo.no)
                        ) {
                            setHasActual(true);
                            if (response.attributes[FieldNames.estimatedActuals.joinIDA]) {
                                const actualID =
                                    response.attributes[FieldNames.estimatedActuals.joinIDA];
                                if (actualID) {
                                    window.api
                                        .getActionByJoinValues(
                                            actualID,
                                            editableFeature.attributes[
                                                FieldNames.featureFields.joinFeature
                                            ]
                                        )
                                        .then((actualObj) => {
                                            setActualsObject(actualObj);
                                        });
                                }
                            }
                        } else {
                            setHasActual(false);
                            setActualsObject(null);
                        }
                    })
                    .catch((error) => {
                        setHasActual(false);
                        setActualsObject(null);
                        ErrorService.addErrorLogEntry(
                            userInfo[FieldNames.userFields.nifcUsername],
                            error
                        );
                    });
            } else {
                setHasActual(false);
                setActualsObject(null);
            }
        }
    };

    useEffect(() => {
        let agencies = [];
        let regions = [];
        let units = [];
        let subUnits = [];
        let tribes = [];

        userRoleInfo.forEach((item) => {
            if (
                item[FieldNames.userRoles.agency] &&
                agencies &&
                !agencies.includes(item[FieldNames.userRoles.agency])
            ) {
                agencies.push(item[FieldNames.userRoles.agency]);
            }
            if (
                item[FieldNames.userRoles.region] &&
                regions &&
                !regions.includes(item[FieldNames.userRoles.region])
            ) {
                regions.push(item[FieldNames.userRoles.region]);
            }
            if (
                item[FieldNames.userRoles.unit] &&
                units &&
                !units.includes(item[FieldNames.userRoles.unit])
            ) {
                units.push(item[FieldNames.userRoles.unit]);
            }
            if (
                item[FieldNames.userRoles.subUnit] &&
                subUnits &&
                !subUnits.includes(item[FieldNames.userRoles.subUnit])
            ) {
                subUnits.push(item[FieldNames.userRoles.subUnit]);
            }
            if (
                item[FieldNames.userRoles.tribe] &&
                tribes &&
                !tribes.includes(item[FieldNames.userRoles.tribe])
            ) {
                tribes.push(item[FieldNames.userRoles.tribe]);
            }
            //Not pulling UnitTypes due to looking it up again.
        });

        setUserAgencies(agencies);
        setUserRegions(regions);
        setUserUnits(units);
        setUserSubUnits(subUnits);
    }, [userRoleInfo]);

    useEffect(() => {
        const fundingSources = DetailedFundingSources.fundingSources;
        const nonContributed = fundingSources.filter(
            (x) => x[FieldNames.fundingSources.isContributed] !== `${FieldNames.yesNo.yes}`
        );
        const contributed = fundingSources.filter(
            (x) => x[FieldNames.fundingSources.isContributed] === `${FieldNames.yesNo.yes}`
        );
        setFundingSourceOptions(nonContributed);
        setContributedFundingSourceOptions(contributed);
    }, [config, rootServiceUrl]);

    useEffect(() => {
        if (rootServiceUrl && config) {
            if (!statusOptions || (statusOptions && statusOptions.length === 0)) {
                LookupService.getStatuses(false)
                    .then((response) => {
                        if (response && response.length > 0) {
                            const statusNames = response.map(
                                (x) => x[FieldNames.statusTable.status]
                            );
                            dispatch(setStatusOptions(statusNames));
                        }
                    })
                    .catch((error) => {
                        ErrorService.addErrorLogEntry(
                            userInfo[FieldNames.userFields.nifcUsername],
                            error
                        );
                    });
            }
        }
    }, [rootServiceUrl, config, statusOptions, setStatusOptions]);

    useEffect(() => {
        if (
            editableFeature?.attributes &&
            editableFeature?.attributes[FieldNames.featureFields.hasActual] === FieldNames.yesNo.yes
        ) {
            if (
                mode &&
                actualsObject &&
                (actualsObject.attributes[FieldNames.featureFields.initiationDate] ||
                    actualsObject.attributes[FieldNames.featureFields.completionDate])
            ) {
                setDisableEditMessage(
                    'Editing not available as the Actual Treatment/Activity has been initiated'
                );
            } else {
                setDisableEditMessage(
                    'Editing not available as the Actual Treatment/Activity has been created'
                );
            }
            setMode(FieldNames.modes.read);
            setDisableEdit(true);
        } else {
            setDisableEdit(false);
        }
    }, [mode, setMode, editableFeature, actualsObject]);

    useEffect(() => {
        if (editableFeature && editableFeature.attributes) {
            let genericFeatureValidationFieldSetTrue = isTreatment
                ? genericTreatmentValidationFieldSetTrue
                : genericActivityValidationFieldSetTrue;

            if (isTreatment && baseTreatmentAttributes === null) {
                setBaseTreatmentAttributes(_.cloneDeep(editableFeature.attributes));
                dispatch(resetTreatmentValidationState());
            }

            if (!isTreatment && baseActivityAttributes === null) {
                setBaseActivityAttributes(_.cloneDeep(editableFeature.attributes));
                dispatch(resetActivityValidationState());
            }

            if (
                editableFeature.attributes[FieldNames.featureFields.id] &&
                (!featureHistoryRows || featureHistoryRows.length === 0)
            ) {
                getFeatureAuditLog(
                    editableFeature.attributes[FieldNames.featureFields.joinID],
                    editableFeature.attributes[FieldNames.featureFields.joinFeature]
                );
            }

            let userRegionValue = '';
            let userDepartmentValue = '';
            let userAgencyValue = '';
            let userUnitValue = '';
            let userSubUnitValue = '';
            let userTribeValue = '';

            const agencyApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.agencyApprovalDate];
            const regionalApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.regionalApprovalDate];
            const departmentApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.departmentApprovalDate];
            const localApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.localApprovalDate];
            const isFunded =
                editableFeature.attributes[FieldNames.featureFields.isFunded] ===
                FieldNames.yesNo.yes;

            setUnApprovalReason(
                editableFeature.attributes[FieldNames.featureFields.unApprovalReason]
                    ? editableFeature.attributes[FieldNames.featureFields.unApprovalReason]
                    : ''
            );
            setUnApprovalDate(
                editableFeature.attributes[FieldNames.featureFields.unApprovalDate]
                    ? editableFeature.attributes[FieldNames.featureFields.unApprovalDate]
                    : ''
            );
            userDepartmentValue = editableFeature.attributes[
                FieldNames.featureFields.fundingDepartment
            ]
                ? editableFeature.attributes[FieldNames.featureFields.fundingDepartment]
                : '';
            setTribeNameValue(
                editableFeature.attributes[FieldNames.featureFields.tribeName]
                    ? editableFeature.attributes[FieldNames.featureFields.tribeName]
                    : ''
            );
            if (isNewFeature) {
                dispatch(
                    genericFeatureValidationFieldSetTrue(FieldNames.featureFields.fundingDepartment)
                );
            }

            if (
                editableFeature?.attributes[FieldNames.featureFields.fundingAgency] !== null &&
                editableFeature?.attributes[FieldNames.featureFields.fundingAgency] !== ''
            ) {
                if (isNewFeature) {
                    dispatch(
                        genericFeatureValidationFieldSetTrue(FieldNames.featureFields.fundingAgency)
                    );
                }
            }

            if (editableFeature.attributes[FieldNames.featureFields.cancelledDate]) {
                let featureFiscalYear = calculateFiscalYearQuarter(
                    editableFeature.attributes[FieldNames.featureFields.cancelledDate],
                    true
                );
                if (featureFiscalYear && featureFiscalYear.toString().length === 4) {
                    //new Date starts at 0 instead of 1 so October is really 09
                    //let currentEditableFiscalYear = calculateEditableFiscalYearOffsetByDay(new Date("2023", "09", "15"));
                    let currentEditableFiscalYear = calculateEditableFiscalYearOffsetByDay(
                        new Date()
                    );
                    let isCurrentFiscalYear = featureFiscalYear
                        ? currentEditableFiscalYear <= featureFiscalYear
                        : true;
                    if (!isCurrentFiscalYear) {
                        setDateFYWarningVal(FieldNames.featureFields.cancelledDate);
                        setWarningContent(
                            'You have entered a date in a previous fiscal year that is no longer available for data entry. Please select a date within the fiscal year(s) available for reporting.'
                        );
                        setWarningTitle('Fiscal Year Warning');
                        setWarningSeverity('success');
                        setWarningButtonText('OK');
                        setWarningOpen(true);
                    }
                }
            }

            if (editableFeature.attributes[FieldNames.featureFields.tribeName]) {
                setTribeNameValue(editableFeature.attributes[FieldNames.featureFields.tribeName]);
            } else {
                setTribeNameValue('');
            }
            if (!tribeNameValue) {
                dispatch(genericFeatureValidationFieldSetTrue(FieldNames.featureFields.tribeName));
            }

            if (!editableFeature.attributes[FieldNames.featureFields.fundingUnitType]) {
                //We update if the value is empty or the current value doesn't match the new value it's being changed to
                if (mode === FieldNames.modes.edit) {
                    //Need to account for when there are userRole values.
                    if (
                        userDepartmentValue &&
                        userAgencyValue &&
                        userRegionValue &&
                        userUnitValue
                    ) {
                        //Look it up since we can't assume their user account has only 1

                        //TODO: Revist this as we probably don't need it.  We do hit the else, but probably don't need to
                        UserHelperService.getUnitEntryByAllFields(
                            userDepartmentValue,
                            userAgencyValue,
                            userRegionValue,
                            userUnitValue,
                            userSubUnitValue,
                            userTribeValue
                        )
                            .then((resp) => {
                                if (resp && resp.length > 0) {
                                    onHandleAttributeChanged(
                                        FieldNames.units.unitType,
                                        resp[0][FieldNames.units.unitType]
                                    );
                                } else {
                                    //Set to empty since none match
                                    onHandleAttributeChanged(FieldNames.units.unitType, '');
                                }
                            })
                            .catch((error) => {
                                ErrorService.addErrorLogEntry(
                                    userInfo[FieldNames.userFields.nifcUsername],
                                    error
                                );
                            });
                    } else {
                        onHandleAttributeChanged(FieldNames.units.unitType, '');
                    }
                }
            }
            let isApprovedAtAll = false;
            let isFullyApprovedAndFunded = false;
            if (
                agencyApprovalValue ||
                regionalApprovalValue ||
                departmentApprovalValue ||
                localApprovalValue
            ) {
                isApprovedAtAll = true;
            }
            if (
                agencyApprovalValue &&
                regionalApprovalValue &&
                departmentApprovalValue &&
                localApprovalValue &&
                isFunded
            ) {
                isFullyApprovedAndFunded = true;
            }
            setIsApproved(isApprovedAtAll);
            setIsApprovedAndFunded(isFullyApprovedAndFunded);

            if (
                !isNewFeature &&
                editableFeature?.attributes &&
                editableFeature.attributes[FieldNames.featureFields.objectID]
            ) {
                let geoType =
                    editableFeature?.attributes?.[FieldNames.featureFields.joinFeature] ??
                    FieldNames.joinFeatureValues.polygon;
                GroupingService.getAllGroupItemsForFeatures(
                    [editableFeature.attributes[FieldNames.featureFields.joinID]],
                    geoType
                ).then((response) => {
                    let groupCount = Number(
                        editableFeature.attributes[FieldNames.featureFields.groupCount]
                    );
                    groupCount = isNaN(groupCount) ? 0 : groupCount;
                    if (response && response.Groups && response.Groups.length !== groupCount) {
                        let tempEditableFeature = Object.assign({}, editableFeature);
                        tempEditableFeature.attributes[FieldNames.featureFields.groupCount] =
                            response.Groups.length;
                        if (response.Groups.length === 0) {
                            tempEditableFeature.attributes[FieldNames.featureFields.hasGroup] =
                                FieldNames.yesNo.no;
                        } else {
                            tempEditableFeature.attributes[FieldNames.featureFields.hasGroup] =
                                FieldNames.yesNo.yes;
                        }
                        let perimeter = tempEditableFeature.geometry;
                        if (!perimeter) {
                            perimeter = tempEditableFeature.perimeter;
                        }

                        FeatureService.updateFeature(tempEditableFeature.attributes)
                            .then((updateResp) => {
                                if (updateResp.updateResults[0].success) {
                                    //Only allowed due to the response.Groups.length !== Number(editableTreatment.attributes[FieldNames.featureFields.groupCount])
                                    //that prevents this from running as an infinite loop
                                    setEditableFeature(tempEditableFeature);
                                }
                            })
                            .catch((error) => {
                                ErrorService.addErrorLogEntry(
                                    userInfo[FieldNames.userFields.nifcUsername],
                                    error
                                );
                            });
                    }
                });
            }

            let reqFields = [];
            reqFields = getRequiredFields(editableFeature, isTreatment, null, userInfo, false);
            setRequiredFields(reqFields);
        }
    }, [
        setIsApproved,
        hasActual,
        userRoleInfo,
        userRegions,
        userUnits,
        userAgencies,
        config,
        isNewFeature,
        userSubUnits,
        mode,
    ]);

    useEffect(() => {
        let baseFeatureAttributes = isTreatment ? baseTreatmentAttributes : baseActivityAttributes;
        if (baseFeatureAttributes === null || editableFeature === null) {
            return;
        }

        haveFeatureValuesChanged(false);
    }, [isTreatment, baseTreatmentAttributes, baseActivityAttributes, editableFeature]);

    useEffect(() => {
        let tempFeature = feature ? Object.assign({}, feature) : null;
        if (tempFeature) {
            const currentfieldInfo = fieldsDefinition[FieldNames.featureFields.class];
            const domain = currentfieldInfo?.domain;
            if (domain) {
                const codedValues = domain.codedValues;
                if (codedValues?.length > 0 && tempFeature?.attributes) {
                    tempFeature.attributes[FieldNames.featureFields.class] = codedValues[0].code;
                }
            }
            if (tempFeature?.attributes) {
                if (!tempFeature.attributes[FieldNames.featureFields.congressionalDistrictNumber]) {
                    tempFeature.attributes[FieldNames.featureFields.congressionalDistrictNumber] =
                        'N/A';
                }

                setEditableFeature(tempFeature);
                setPlannedFeature(tempFeature);
                setProgramValue(tempFeature.attributes[FieldNames.featureFields.program]);
                setClassValue(tempFeature.attributes[FieldNames.featureFields.class]);
                setCategoryValue(tempFeature.attributes[FieldNames.featureFields.category]);
                setTypeValue(tempFeature.attributes[FieldNames.featureFields.type]);
                setSubTypeValue(tempFeature.attributes[FieldNames.featureFields.subType]);
                setUnitOfMeasureValue(
                    tempFeature.attributes[FieldNames.featureFields.unitOfMeasure]
                );
            }
        }
    }, [
        isTreatment,
        feature,
        setPlannedFeature,
        userRegions,
        userUnits,
        config,
        isNewFeature,
        userSubUnits,
        userAgencies,
        rootServiceUrl,
    ]);

    useEffect(() => {
        let tempFeature = Object.assign({}, editableFeature);
        if (
            tempFeature?.attributes &&
            tempFeature.attributes[FieldNames.featureFields.status] !==
                FieldNames.statuses.cancelled
        ) {
            if (mode === FieldNames.modes.edit) {
                const missingFieldsCount = getAllMissingFieldsCount(
                    tempFeature,
                    isTreatment,
                    userInfo,
                    false
                );
                if (
                    !tempFeature.attributes[FieldNames.featureFields.status] ||
                    (tempFeature.attributes[FieldNames.featureFields.status] ===
                        FieldNames.statuses.notStarted &&
                        tempFeature.attributes[FieldNames.featureFields.hasActual] !==
                            FieldNames.yesNo.yes)
                ) {
                    tempFeature.attributes[FieldNames.featureFields.status] = getStatus(
                        tempFeature,
                        tempFeature.attributes[FieldNames.featureFields.hasActual],
                        false,
                        missingFieldsCount
                    );
                }
            }

            setEditableFeature(tempFeature);
        }
    }, [mode, userInfo, isTreatment]);

    useEffect(() => {
        let obj = {};
        let allIds = groupDetails?.map((element) => {
            return element.identifier;
        });
        allIds?.forEach((id) => {
            if (id === FieldNames.panels.costs) {
                obj[id] = 'false';
            } else {
                obj[id] = 'true';
            }
        });
        setExpandedItems(obj);
    }, [groupDetails, isTreatment]);

    const title = (detail) => {
        const isPostFire = isFeaturePostFire(editableFeature?.attributes);
        return (
            <div className='incident-details-panel-header'>
                <h4>{detail.title}</h4>
                {detail.identifier === FieldNames.panels.costs && (
                    <Tooltip
                        title={
                            isNewFeature
                                ? mode === FieldNames.modes.edit && !isApproved
                                    ? 'Save first to Manage Costs'
                                    : 'Save first to View Costs'
                                : isPostFire &&
                                    !editableFeature.attributes[FieldNames.featureFields.program] &&
                                    mode === FieldNames.modes.edit
                                  ? 'Select a Program before adding costs'
                                  : ''
                        }
                    >
                        <span>
                            <Chip
                                className='required-field-status-chip'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (getAllUnsavedFieldsCount(editableFeature) > 0) {
                                        setWarningContent(
                                            'There is unsaved data that must be saved before accessing manage costs'
                                        );
                                        setWarningTitle('Unsaved Data Warning');
                                        setWarningButtonText('Close');
                                        setWarningSeverity('warning');
                                        setWarningOpen(true);
                                    } else {
                                        onManageCosts();
                                    }
                                }}
                                disabled={
                                    isNewFeature ||
                                    (isPostFire &&
                                        (!editableFeature?.attributes?.[
                                            FieldNames.featureFields.program
                                        ] ||
                                            !editableFeature?.attributes?.[
                                                FieldNames.featureFields.category
                                            ]) &&
                                        mode === FieldNames.modes.edit)
                                }
                                style={{
                                    backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                    marginLeft: 3,
                                    zIndex: 20,
                                }}
                                avatar={
                                    <Avatar
                                        style={{
                                            fontSize: '12px',
                                            width: '22px',
                                            height: '22px',
                                            backgroundColor: ColorConstants.AvatarColor,
                                            color: ColorConstants.WhiteAlien,
                                        }}
                                    >
                                        <AttachMoneyIcon />
                                    </Avatar>
                                }
                                label={
                                    <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                        {mode === FieldNames.modes.edit && !isApproved
                                            ? 'Manage Costs'
                                            : 'View Costs'}
                                    </span>
                                }
                            />
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    };

    const closeUnApprovalEditor = () => {
        setUnApprovalEditorOpen(false);
    };

    const rederiveFeatureCallback = (
        feature,
        isTreatment,
        isFeatureActuals,
        isPoint,
        wfftsAttributes,
        legacyAttributes
    ) => {
        const resetAcreage = false;
        dispatch(setShowGlobalLoader(true));
        rederiveFeatureInfo(
            feature,
            isTreatment,
            isFeatureActuals,
            isPoint,
            wfftsAttributes,
            legacyAttributes,
            resetAcreage
        );
    };

    const checkPointManagementActivityChange = (feature, newCategory, newUnitType) => {
        if (
            feature &&
            feature.attributes &&
            feature.attributes[FieldNames.featureFields.isPoint] === 'Yes' &&
            !isTreatment
        ) {
            if (
                (feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                    FieldNames.unitTypes.national ||
                    feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                        FieldNames.unitTypes.regional) &&
                categoryValue === FieldNames.categoriesForChecking.programManagement &&
                (!(
                    newUnitType === FieldNames.unitTypes.national ||
                    newUnitType === FieldNames.unitTypes.regional
                ) ||
                    newCategory !== FieldNames.categoriesForChecking.programManagement)
            ) {
                // Feature is being altered from the conditions. Feature is always a point per original FUELS-504 conditions
                rederiveFeatureCallback(feature, isTreatment, false, true, null, null);
            } else if (
                (!(
                    feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                        FieldNames.unitTypes.national ||
                    feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                        FieldNames.unitTypes.regional
                ) ||
                    categoryValue !== FieldNames.categoriesForChecking.programManagement) &&
                (newUnitType === FieldNames.unitTypes.national ||
                    newUnitType === FieldNames.unitTypes.regional) &&
                newCategory === FieldNames.categoriesForChecking.programManagement
            ) {
                // Feature is being altered to the conditions. Set the location fields to the funding fields.
                setLocationFieldsToFundingFields(feature);
                feature.attributes[FieldNames.featureFields.locationDepartmentLocked] =
                    FieldNames.yesNo.no;
                feature.attributes[FieldNames.featureFields.locationAgencyLocked] =
                    FieldNames.yesNo.no;
                feature.attributes[FieldNames.featureFields.locationRegionLocked] =
                    FieldNames.yesNo.no;
                feature.attributes[FieldNames.featureFields.locationUnitLocked] =
                    FieldNames.yesNo.no;
            }
        }
    };

    const setLocationFieldsToFundingFields = (feature) => {
        // Check if feature is activity/point, program management category, and unit type of national/regional
        if (feature && feature.attributes) {
            // Note: The departmentValue through unitValue state variables are not updated as of this point. Use the feature attributes if possible.
            const fundingDepVal = feature.attributes[FieldNames.featureFields.fundingDepartment]
                ? feature.attributes[FieldNames.featureFields.fundingDepartment].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingDepartment]
                : null;
            const fundingAgeVal = feature.attributes[FieldNames.featureFields.fundingAgency]
                ? feature.attributes[FieldNames.featureFields.fundingAgency].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingAgency]
                : null;
            const fundingRegVal = feature.attributes[FieldNames.featureFields.fundingRegion]
                ? feature.attributes[FieldNames.featureFields.fundingRegion].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingRegion]
                : null;
            const fundingUnitVal = feature.attributes[FieldNames.featureFields.fundingUnit]
                ? feature.attributes[FieldNames.featureFields.fundingUnit].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingUnit]
                : null;

            feature.attributes[FieldNames.featureFields.department] = fundingDepVal;
            setLocationDepartmentValue(fundingDepVal);
            feature.attributes[FieldNames.featureFields.agency] = fundingAgeVal;
            setLocationAgencyValue(fundingAgeVal);
            feature.attributes[FieldNames.featureFields.region] = fundingRegVal;
            setLocationRegionValue(fundingRegVal);
            feature.attributes[FieldNames.featureFields.unit] = fundingUnitVal;
            setLocationUnitValue(fundingUnitVal);
        }
    };

    const haveFeatureValuesChanged = (isFromFunding) => {
        let baseFeatureAttributes = isTreatment ? baseTreatmentAttributes : baseActivityAttributes;
        let genericFeatureValidationFieldSetFalse = isTreatment
            ? genericTreatmentValidationFieldSetFalse
            : genericActivityValidationFieldSetFalse;
        let genericFeatureValidationFieldSetTrue = isTreatment
            ? genericTreatmentValidationFieldSetTrue
            : genericActivityValidationFieldSetTrue;
        let resetFeatureValidationState = isTreatment
            ? resetTreatmentValidationState
            : resetActivityValidationState;

        if (baseFeatureAttributes === null || editableFeature === null) {
            return false;
        }
        dispatch(resetFeatureValidationState());
        const isNewCheck = baseFeatureAttributes[FieldNames.featureFields.isNew] && isNewFeature;
        for (let prop in editableFeature.attributes) {
            const editableProp = editableFeature.attributes[prop];
            const baseProp = baseFeatureAttributes[prop];

            // Note: We are using soft equality to catch instances where numbers are stored as strings in the db (I.E. Latitude/Longitude)
            if (
                (isNewCheck === true && editableProp) ||
                (baseProp && editableProp && baseProp != editableProp) ||
                (!baseProp && editableProp) ||
                (baseProp && !editableProp)
            ) {
                if (prop === FieldNames.featureFields.wbsID) {
                    if (
                        editableFeature.attributes[
                            FieldNames.featureFields.fundingAgency
                        ]?.includes(FieldNames.agencies.blm)
                    ) {
                        const appendedValue = FieldNames.wbsPrefixes.blm + editableProp + '0000';
                        if (appendedValue === baseProp) {
                            dispatch(genericFeatureValidationFieldSetFalse(prop));
                        } else {
                            dispatch(genericFeatureValidationFieldSetTrue(prop));
                        }
                    }
                }
                dispatch(genericFeatureValidationFieldSetTrue(prop));
            } else {
                dispatch(genericFeatureValidationFieldSetFalse(prop));
            }
        }
        if (editableFeature?.attributes && isFromFunding) {
            dispatch(
                genericFeatureValidationFieldSetTrue(FieldNames.featureFields.fundingDepartment)
            );
            dispatch(genericFeatureValidationFieldSetTrue(FieldNames.featureFields.fundingAgency));
            dispatch(genericFeatureValidationFieldSetTrue(FieldNames.featureFields.fundingRegion));
            dispatch(genericFeatureValidationFieldSetTrue(FieldNames.featureFields.fundingUnit));
        }
    };

    const getFeatureAuditLog = (joinID, joinFeature) => {
        if (joinID) {
            AuditLogService.getEntityAuditLog(joinFeature, joinID).then((auditRows) => {
                if (auditRows && auditRows.length > 0) {
                    const mappedRows = auditRows.map((auditRow) => {
                        try {
                            return {
                                id: auditRow.OBJECTID,
                                ModifiedBy: auditRow.ModifiedBy,
                                ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                                Modification: JSON.parse(auditRow.AuditEntryJson)[0],
                            };
                        } catch (e) {
                            return {
                                id: auditRow.OBJECTID,
                                ModifiedBy: auditRow.ModifiedBy,
                                ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                                Modification: auditRow.AuditEntryJson,
                            };
                        }
                    });
                    setFeatureHistoryRows(mappedRows);
                } else {
                    setFeatureHistoryRows([]);
                }
            });
        }
    };

    const addFeatureAuditLogEntry = (feature, joinFeature, featureID, isNew, reloadGrid) => {
        return new Promise(function (resolve) {
            const changedFields = isTreatment
                ? treatmentValidationUnsavedValues
                : activityValidationUnsavedValues;
            const changeString = getLogForFieldUpdatesOnExistingFeature(changedFields);
            if (isUnApprovalRequested) {
                const date = new Date();
                const day = date.getDate();
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                AuditLogService.addAuditLogEntry(
                    feature,
                    featureID,
                    joinFeature,
                    userInfo[FieldNames.userFields.nifcUsername],
                    [
                        isNew ? 'Feature was created.' : changeString,
                        `Unapproval Requested on ${month}-${day}-${year} by ${userInfo[FieldNames.userFields.nifcUsername]}, Reason: ${unApprovalReason}`,
                    ]
                ).then((response) => {
                    if (!isNew && reloadGrid) {
                        getFeatureAuditLog(featureID, joinFeature);
                    }
                    resolve('OK');
                });
            } else {
                AuditLogService.addAuditLogEntry(
                    feature,
                    featureID,
                    joinFeature,
                    userInfo[FieldNames.userFields.nifcUsername],
                    [isNew ? 'Feature was created' : changeString]
                ).then((response) => {
                    if (!isNew && reloadGrid) {
                        getFeatureAuditLog(featureID, joinFeature);
                    }
                    resolve('OK');
                });
            }
        });
    };

    const establishGridColumns = () => {
        let columns = [];
        featureAuditLogGridColDefinitions.forEach((colDef) => {
            columns.push({
                field: colDef.value,
                headerName: colDef.header,
                type: colDef.type,
                hideable: colDef.hideable,
                sortable: colDef.sortable,
                editable: colDef.editable,
                flex: colDef.flex,
                align: colDef.align,
                disableExport: colDef.disableExport,
                valueGetter: (value) => {
                    return value; // Used for formatting string values I.E. currency formatting
                },
            });
        });

        setFeatureHistoryCols(columns);
    };

    const getCurrentApprovalValues = (attributeName, attributeValue, itemClass) => {
        let agencyApprovalValue;
        let regionalApprovalValue;
        let departmentApprovalValue;
        let localApprovalValue;
        let isFunded = false;
        if (attributeName) {
            agencyApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.agencyApprovalDate];
            regionalApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.regionalApprovalDate];
            departmentApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.departmentApprovalDate];
            localApprovalValue =
                editableFeature.attributes[FieldNames.featureFields.localApprovalDate];
            isFunded = editableFeature.attributes[FieldNames.featureFields.isFunded];

            if (attributeName === FieldNames.featureFields.agencyApprovalDate) {
                agencyApprovalValue = attributeValue;
            } else if (attributeName === FieldNames.featureFields.regionalApprovalDate) {
                regionalApprovalValue = attributeValue;
            } else if (attributeName === FieldNames.featureFields.departmentApprovalDate) {
                departmentApprovalValue = attributeValue;
            } else if (attributeName === FieldNames.featureFields.localApprovalDate) {
                localApprovalValue = attributeValue;
            } else if (attributeName === FieldNames.featureFields.isFunded) {
                isFunded = attributeValue;
            }
        }

        return {
            agency: agencyApprovalValue,
            region: regionalApprovalValue,
            department: departmentApprovalValue,
            local: localApprovalValue,
            funded: isFunded,
        };
    };

    const isApprovedPartially = (attributeName, attributeValue, itemClass) => {
        const currentValues = getCurrentApprovalValues(attributeName, attributeValue, itemClass);
        if (
            currentValues.agency ||
            currentValues.region ||
            currentValues.department ||
            currentValues.local
        ) {
            return true;
        } else {
            return false;
        }
    };

    const isFullyApprovedAndFunded = (attributeName, attributeValue, itemClass) => {
        const currentValues = getCurrentApprovalValues(attributeName, attributeValue, itemClass);
        if (
            currentValues.agency &&
            currentValues.region &&
            currentValues.department &&
            currentValues.local
        ) {
            return true;
        } else {
            return false;
        }
    };

    const isFullyApproved = (attributeName, attributeValue, itemClass) => {
        const currentValues = getCurrentApprovalValues(attributeName, attributeValue, itemClass);
        if (
            currentValues.agency &&
            currentValues.region &&
            currentValues.department &&
            currentValues.local
        ) {
            return true;
        } else {
            return false;
        }
    };

    const handleClose = () => {
        setWarningOpen(false);
        setWarningContent('');
        setWarningContent('');
        setWarningTitle('');
        setWarningSeverity('success');
        setWarningButtonText('');
    };

    const handleContinue = () => {
        setWarningOpen(false);
        onHandleDateWithFYFQChanged(dateFYWarningVal, null);
    };

    const calculateEditableFiscalYearOffsetByDay = (value) => {
        //Fiscal Quarters are Q1 = 10/1 to 12/31, Q2 = 1/1 to 3/31, Q3 = 4/1 to 6/31, Q4 = 7/1 to 9/31
        if (value) {
            const month = value.getMonth() + 1;
            const year = value.getFullYear();
            const day = value.getDate();

            //Default state is for the current year fiscal year. The current year and fiscal year match
            let fiscalYear = year;

            // We are checking the 15th specifically for locking editing / displaying a warning. Normally FY rollover starts on October 1st
            if (month === 10 && day <= 15) {
                fiscalYear = year;
            } else if ((month === 10 && day > 15) || (month >= 11 && month <= 12)) {
                fiscalYear = year + 1;
            }
            return fiscalYear;
        }
    };

    const onHandleDateWithFYFQChanged = (attributeName, attributeValue) => {
        let updatedProp = {};
        let year = calculateFiscalYearQuarter(attributeValue, true);
        let quarter = calculateFiscalYearQuarter(attributeValue, false);
        if (attributeName === FieldNames.featureFields.completionDate) {
            let initValue = attributeValue;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            } else {
                initValue = null;
            }
            updatedProp[attributeName] = initValue;
            updatedProp[FieldNames.featureFields.completionFiscalQuarter] = quarter;
            updatedProp[FieldNames.featureFields.completionFiscalYear] = year;
        } else if (attributeName === FieldNames.featureFields.initiationDate) {
            let initValue = attributeValue;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            } else {
                initValue = null;
            }
            updatedProp[attributeName] = initValue;
            updatedProp[FieldNames.featureFields.initiationFiscalQuarter] = quarter;
            updatedProp[FieldNames.featureFields.initiationFiscalYear] = year;
            if (!editableFeature.attributes[FieldNames.featureFields.originalInitiationDate]) {
                updatedProp[FieldNames.featureFields.originalInitiationDate] = initValue;
                updatedProp[FieldNames.featureFields.originalInitiationFiscalQuarter] = quarter;
                updatedProp[FieldNames.featureFields.originalInitiationFiscalYear] = year;
            }
        } else if (attributeName === FieldNames.featureFields.cancelledDate) {
            let initValue = attributeValue;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            } else {
                initValue = null;
            }
            if (
                attributeName === FieldNames.featureFields.cancelledDate &&
                editableFeature &&
                editableFeature.attributes
            ) {
                let removingCancelledDate = false;
                if (!editableFeature.attributes[attributeName] && initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.yes;
                } else if (editableFeature.attributes[attributeName] && !initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.no;
                    removingCancelledDate = true;
                }
                setDoClearCancelLogic(removingCancelledDate);
            }

            updatedProp[attributeName] = initValue;
        }
        if (editableFeature?.attributes) {
            const updatedAttributes = {
                attributes: Object.assign({}, editableFeature.attributes, updatedProp),
            };

            const currentStatus =
                attributeName === FieldNames.featureFields.status
                    ? attributeValue
                    : updatedAttributes.attributes[FieldNames.featureFields.status];
            if (currentStatus !== FieldNames.statuses.cancelled) {
                const missingFieldsCount = getAllMissingFieldsCount(
                    updatedAttributes,
                    isTreatment,
                    userInfo,
                    false
                );
                updatedAttributes.attributes[FieldNames.featureFields.status] = getStatus(
                    updatedAttributes,
                    updatedAttributes.attributes[FieldNames.featureFields.hasActual],
                    false,
                    missingFieldsCount
                );
            }
            const updatedFeature = Object.assign({}, editableFeature, updatedAttributes);
            setEditableFeature(updatedFeature);
        }
    };

    const finalFeatureUpdate = (editableFeature, isTreatment, updatedProp) => {
        let fields = FieldNames.featureFields;
        if (updatedProp) {
            const catProp = updatedProp.hasOwnProperty(fields.category);
            const fundUnitTypeProp = updatedProp.hasOwnProperty(fields.fundingUnitType);
            if (catProp || fundUnitTypeProp) {
                // FUELS-504: Change was made to category and/or fundingUnitType, check for point management activity
                checkPointManagementActivityChange(
                    editableFeature,
                    catProp
                        ? updatedProp[fields.category]
                            ? updatedProp[fields.category]
                            : ''
                        : editableFeature.attributes[fields.category],
                    fundUnitTypeProp
                        ? updatedProp[fields.fundingUnitType]
                            ? updatedProp[fields.fundingUnitType]
                            : ''
                        : editableFeature.attributes[fields.fundingUnitType]
                );
            }
        }

        const updatedAttributes = {
            attributes: Object.assign({}, editableFeature?.attributes, updatedProp),
        };
        const missingFieldsCount = getAllMissingFieldsCount(
            updatedAttributes,
            isTreatment,
            userInfo,
            false,
            null
        );
        updatedAttributes.attributes[fields.status] = getStatus(
            updatedAttributes,
            updatedAttributes.attributes[fields.hasActual],
            false,
            missingFieldsCount
        );
        const updatedFeature = Object.assign({}, editableFeature, updatedAttributes);
        setEditableFeature(updatedFeature);

        let reqFields = [];
        reqFields = getRequiredFields(updatedFeature, isTreatment, null, userInfo, false);
        setRequiredFields(reqFields);
    };

    const onHandleAttributeChanged = (attributeName, attributeValue, projectInfo = null) => {
        let updatedProp = {};
        let fields = FieldNames.featureFields;
        let tempEditableFeature = {
            attributes: null,
            geometry: null,
        };
        tempEditableFeature.attributes = _.cloneDeep(editableFeature.attributes);
        if (editableFeature?.geometry) {
            tempEditableFeature.geometry = editableFeature.geometry;
        } else if (editableFeature?.perimeter) {
            tempEditableFeature.geometry = editableFeature.perimeter;
        }
        let classType = 'treatment';
        if (!isTreatment) {
            classType = 'activity';
        }

        if (
            attributeName === fields.localApprovalDate ||
            attributeName === fields.regionalApprovalDate ||
            attributeName === fields.departmentApprovalDate ||
            attributeName === fields.agencyApprovalDate ||
            attributeName === fields.cancelledDate
        ) {
            let initValue = null;
            if (attributeValue) {
                initValue = attributeValue.getTime();
            }

            if (
                attributeName === fields.cancelledDate &&
                tempEditableFeature &&
                tempEditableFeature.attributes
            ) {
                let removingCancelledDate = false;
                if (!tempEditableFeature.attributes[attributeName] && initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.yes;
                } else if (tempEditableFeature.attributes[attributeName] && !initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.no;
                    removingCancelledDate = true;
                }
                setDoClearCancelLogic(removingCancelledDate);
            }

            updatedProp[attributeName] = initValue;
        } else {
            if (costFields && costFields.includes(attributeName)) {
                updatedProp[attributeName] =
                    attributeValue !== '' ? removeCommas(attributeValue) : null;
            } else {
                updatedProp[attributeName] = attributeValue !== '' ? attributeValue : null;
            }
        }

        if (tempEditableFeature && tempEditableFeature.attributes) {
            const isPartiallyApproved = isApprovedPartially(
                attributeName,
                attributeValue,
                classType
            );
            setIsApproved(isPartiallyApproved);
            setIsApprovedAndFunded(
                isFullyApprovedAndFunded(attributeName, attributeValue, classType)
            );

            if (
                attributeName === fields.localApprovalDate ||
                attributeName === fields.regionalApprovalDate ||
                attributeName === fields.departmentApprovalDate ||
                attributeName === fields.agencyApprovalDate
            ) {
                const isAllLevelsApproved = isFullyApproved(
                    attributeName,
                    attributeValue,
                    classType
                );
                if (isAllLevelsApproved) {
                    if (
                        tempEditableFeature.attributes[fields.isApproved] !== FieldNames.yesNo.yes
                    ) {
                        updatedProp[fields.isApproved] = FieldNames.yesNo.yes;
                    }

                    if (tempEditableFeature.attributes[fields.isFunded] !== FieldNames.yesNo.yes) {
                        updatedProp[fields.isFunded] = FieldNames.yesNo.yes;
                    }
                } else {
                    if (
                        tempEditableFeature.attributes[fields.isApproved] === FieldNames.yesNo.yes
                    ) {
                        updatedProp[fields.isApproved] = FieldNames.yesNo.no;
                    }

                    if (tempEditableFeature.attributes[fields.isFunded] === FieldNames.yesNo.yes) {
                        updatedProp[fields.isFunded] = FieldNames.yesNo.no;
                    }

                    if (
                        tempEditableFeature.attributes[fields.unApprovalDate] &&
                        !isPartiallyApproved
                    ) {
                        updatedProp[fields.unApprovalDate] = null;
                        updatedProp[fields.unApprovalReason] = null;
                        tempEditableFeature.attributes[fields.unApprovalDate] = null;
                    }
                }
            }

            if (attributeName === fields.status) {
                if (attributeValue !== FieldNames.statuses.cancelled) {
                    setIsCancelled(false);
                } else {
                    setIsCancelled(true);
                    updatedProp[fields.cancelledDate] = new Date().getTime();
                }
            } else {
                //Set the status
                let filterFields = [];
                if (attributeName === fields.cancelledDate && !attributeValue) {
                    filterFields.push(fields.statusReason);
                }
            }

            if (attributeName === fields.vegDeparturePercentageManual) {
                updatedProp[fields.vegDeparturePercentageManual] = attributeValue;
                updatedProp[fields.isVegetationManual] = FieldNames.yesNo.yes;
            }

            if (attributeName === fields.unApprovalReason) {
                updatedProp[fields.unApprovalReason] = attributeValue;
                updatedProp[fields.unApprovalDate] = getUTCMoment(unApprovalDate).valueOf();
            }

            if (attributeName === fields.unApprovalDate && !attributeValue) {
                updatedProp[fields.unApprovalReason] = null;
                updatedProp[fields.unApprovalDate] = null;
            }

            if (attributeName === fields.program) {
                updatedProp[fields.category] = '';
                updatedProp[fields.type] = '';
                updatedProp[fields.subType] = '';
                updatedProp[fields.measureAmount] = null;
                updatedProp[fields.unitOfMeasure] = '';
            }

            if (attributeName === fields.category) {
                updatedProp[fields.type] = '';
                updatedProp[fields.subType] = '';
                updatedProp[fields.measureAmount] = null;
                updatedProp[fields.unitOfMeasure] = '';
            }

            if (attributeName === fields.type) {
                updatedProp[fields.subType] = '';
                updatedProp[fields.measureAmount] = null;
                updatedProp[fields.unitOfMeasure] = '';
            }

            if (attributeName === fields.subType) {
                updatedProp[fields.measureAmount] = null;
                updatedProp[fields.unitOfMeasure] = '';
            }

            if (attributeName === fields.class) {
                //reset every time class changes
                updatedProp[fields.program] = '';
                updatedProp[fields.category] = '';
                updatedProp[fields.type] = '';
                updatedProp[fields.subType] = '';
                updatedProp[fields.measureAmount] = null;
                updatedProp[fields.unitOfMeasure] = '';
            }

            if (attributeName === fields.isInteragency) {
                if (
                    attributeValue === FieldNames.yesNo.no &&
                    (interagencySelected?.length || otherInteragencySelected?.length)
                ) {
                    const allInteragencySelectedForDeletion = interagencySelected.filter(
                        (x) => x?.[FieldNames.interagencyFields.objectID]
                    );
                    const allOtherInteragencySelectedForDeletion = otherInteragencySelected.filter(
                        (x) => x?.[FieldNames.interagencyFields.objectID]
                    );
                    const allSelectedForDeletion = [
                        ...allInteragencySelectedForDeletion,
                        ...allOtherInteragencySelectedForDeletion,
                    ];
                    setItemsToDelete(allSelectedForDeletion);
                    setInteragencySelected([]);
                    setOtherInteragencySelected([]);
                }
            }

            if (attributeName === FieldNames.featureFields.projectID) {
                if (projectInfo) {
                    updatedProp[FieldNames.featureFields.projectFireName] =
                        projectInfo[FieldNames.projectFields.fireName];
                    updatedProp[FieldNames.featureFields.projectName] =
                        projectInfo[FieldNames.projectFields.name];
                    updatedProp[FieldNames.featureFields.wildfireAcres] =
                        projectInfo[FieldNames.projectFields.wildfireAcres];
                    updatedProp[FieldNames.featureFields.fireCode] =
                        projectInfo[FieldNames.projectFields.fireCode];
                    updatedProp[FieldNames.featureFields.irwinID] =
                        projectInfo[FieldNames.projectFields.irwinID];
                    updatedProp[FieldNames.featureFields.fireDiscoveryDate] =
                        projectInfo[FieldNames.projectFields.fireDiscoveryDate];
                    updatedProp[FieldNames.featureFields.containmentDate] =
                        projectInfo[FieldNames.projectFields.containmentDate];
                    updatedProp[FieldNames.featureFields.ignitionFiscalYear] =
                        projectInfo[FieldNames.projectFields.ignitionFiscalYear];
                    updatedProp[FieldNames.featureFields.projectNotes] =
                        projectInfo[FieldNames.projectFields.notes];
                    updatedProp[FieldNames.featureFields.projectEntityType] =
                        projectInfo[FieldNames.projectFields.entityType];
                    updatedProp[FieldNames.featureFields.isFirePerimeter] =
                        projectInfo[FieldNames.projectFields.isFirePerimeter];
                } else {
                    updatedProp[FieldNames.featureFields.projectFireName] = '';
                    updatedProp[FieldNames.featureFields.projectName] = '';
                    updatedProp[FieldNames.featureFields.wildfireAcres] = '';
                    updatedProp[FieldNames.featureFields.fireCode] = '';
                    updatedProp[FieldNames.featureFields.irwinID] = '';
                    updatedProp[FieldNames.featureFields.fireDiscoveryDate] = '';
                    updatedProp[FieldNames.featureFields.containmentDate] = '';
                    updatedProp[FieldNames.featureFields.ignitionFiscalYear] = '';
                    updatedProp[FieldNames.featureFields.projectNotes] = '';
                    updatedProp[FieldNames.featureFields.projectEntityType] = '';
                    updatedProp[FieldNames.featureFields.isFirePerimeter] = '';
                }
            }

            if (
                tempEditableFeature.attributes[fields.isFunded] === FieldNames.yesNo.yes &&
                updatedProp[fields.isApproved] === FieldNames.yesNo.no
            ) {
                updatedProp[fields.isFunded] = FieldNames.yesNo.no;
            }
            finalFeatureUpdate(tempEditableFeature, isTreatment, updatedProp);
        }
    };

    const onHandleEditClicked = () => {
        setMode(FieldNames.modes.edit); //TODO: Throw into enum or something
    };

    const findOcc = (arr, key) => {
        let arr2 = [];

        arr.forEach((x) => {
            // Checking if there is any object in arr2
            // which contains the key value
            if (
                arr2.some((val) => {
                    return val[key] === x[key];
                })
            ) {
                // If yes! then increase the occurrence by 1
                arr2.forEach((k) => {
                    if (k[key] === x[key]) {
                        k['occurrence']++;
                    }
                });
            } else {
                // If not! Then create a new object initialize
                // it with the present iteration key's value and
                // set the occurrence to 1
                let a = {};
                a[key] = x[key];
                a['occurrence'] = 1;
                arr2.push(a);
            }
        });

        return arr2;
    };

    const archiveGroups = () => {
        const groupIds = featureGroups.map(
            (x) => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID]
        );
        if (groupIds && groupIds.length > 0) {
            GroupingService.getAllGroupItemsByGroupIds(groupIds).then((items) => {
                const groupOccurrences = findOcc(
                    items,
                    FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID
                );
                const deleteGroupIds = [];
                const deleteGroups = [];
                groupOccurrences.forEach((occ) => {
                    if (occ.occurrence < 3) {
                        deleteGroupIds.push(
                            occ[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID]
                        );
                    }
                });
                GroupingService.getGroupsByIDs(deleteGroupIds).then((groups) => {
                    groups.forEach((group) => {
                        group[FieldNames.treatmentActivityGroups.isArchived] = 'Yes';
                        const obj = {
                            attributes: group,
                        };
                        deleteGroups.push(obj);
                    });
                    GroupingService.archiveGroups(deleteGroups);
                });
            });
        }
    };

    const archiveGroupItems = () => {
        if (associatedCount > 0) {
            let deleteGroupItem = [];
            let feature = editableFeature;
            let fields = FieldNames.featureFields;
            if (feature?.attributes && feature.attributes[fields.objectID]) {
                let geoType =
                    editableFeature?.attributes?.[FieldNames.featureFields.joinFeature] ??
                    FieldNames.joinFeatureValues.polygon;
                GroupingService.getAllGroupItemsForFeatures(
                    [feature.attributes[fields.joinID]],
                    geoType
                ).then((result) => {
                    if (result && result.GroupItems && result.GroupItems.length > 0) {
                        result.GroupItems.forEach((groupItem) => {
                            groupItem[FieldNames.treatmentActivityGroupItems.isArchived] = 'Yes';
                            const obj = {
                                attributes: groupItem,
                            };
                            deleteGroupItem.push(obj);
                        });
                        GroupingService.archiveGroupItems(deleteGroupItem);
                    }
                });
            }
        }
    };

    const archiveFeature = (perimeter, newFeatureAttributes, isCurrentTreatment) => {
        if (!hasActual) {
            FeatureService.updateFeature(newFeatureAttributes).then((response) => {
                if (response.updateResults[0].success) {
                    AuditLogService.addAuditLogEntry(
                        newFeatureAttributes,
                        newFeatureAttributes[FieldNames.featureFields.joinID],
                        newFeatureAttributes[FieldNames.featureFields.joinFeature],
                        userInfo[FieldNames.userFields.nifcUsername],
                        [`Feature was manually archived`]
                    ).then((auditResp) => {
                        const geometryType =
                            newFeatureAttributes[FieldNames.featureFields.joinFeature];
                        const isPostFire = isFeaturePostFire(newFeatureAttributes);
                        setMode(FieldNames.modes.read);
                        setFeature(null);
                        setSnackbarMessage(`${isTreatment ? `Treatment` : `Activity`} deleted.`);
                        setSnackbarOpen(true);
                        setEditableFeature(null);
                        navigate('/home');
                        emitCustomEvent(Events.RefreshLayer, {
                            geo: perimeter,
                            geometryType: geometryType,
                            isPostFire: isPostFire,
                            featureClass: isTreatment
                                ? FieldNames.classTypes.treatment
                                : FieldNames.classTypes.activity,
                            attributes: newFeatureAttributes,
                            isProject: false,
                        });
                        emitCustomEvent(Events.ClearModifiedLayer);
                    });
                } else {
                    setSnackbarMessage('Error deleting.');
                    setSnackbarOpen(true);
                }
            });
        }
    };

    const onHandleDeleteClicked = () => {
        let perimeter;
        let newFeatureAttributes;
        editableFeature.attributes[FieldNames.featureFields.isArchived] = FieldNames.yesNo.yes;
        newFeatureAttributes = editableFeature.attributes;
        perimeter = editableFeature.geometry;
        if (!perimeter) {
            perimeter = editableFeature.perimeter;
        }
        let isError = false;
        if (associatedCount > 0) {
            try {
                archiveGroupItems();
                archiveGroups();
            } catch (err) {
                if (err) {
                    isError = true;
                }
            }
        }
        if (!isError) {
            archiveFeature(perimeter, newFeatureAttributes, isTreatment);
        } else {
            setSnackbarMessage('Error deleting. Not Deleted!');
            setSnackbarOpen(true);
        }
    };

    const cloneFeature = (newFeatureAttributes, attributes, isParentTreatment, geoType, geoID) => {
        if (attributes) {
            newFeatureAttributes = _.cloneDeep(attributes);
            newFeatureAttributes[FieldNames.featureFields.joinFeature] = geoType;
            newFeatureAttributes[FieldNames.featureFields.joinID] = geoID;
            newFeatureAttributes[FieldNames.featureFields.parentID] =
                attributes[FieldNames.featureFields.id];
            newFeatureAttributes[FieldNames.featureFields.isParentTreatment] = isParentTreatment
                ? FieldNames.yesNo.yes
                : FieldNames.yesNo.no;
            newFeatureAttributes[FieldNames.featureFields.localApprovalDate] = null;
            newFeatureAttributes[FieldNames.featureFields.regionalApprovalDate] = null;
            newFeatureAttributes[FieldNames.featureFields.departmentApprovalDate] = null;
            newFeatureAttributes[FieldNames.featureFields.unApprovalDate] = null;
            newFeatureAttributes[FieldNames.featureFields.unApprovalReason] = null;
            newFeatureAttributes[FieldNames.featureFields.agencyApprovalDate] = null;
            newFeatureAttributes[FieldNames.featureFields.vegDeparturePercentageDerived] = null;
            newFeatureAttributes[FieldNames.featureFields.vegDeparturePercentageManual] = null;
            newFeatureAttributes[FieldNames.featureFields.isVegetationManual] = FieldNames.yesNo.no;
            newFeatureAttributes[FieldNames.featureFields.fundingDepartment] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingAgency] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingRegion] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingUnit] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingSubUnit] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingTribe] = null;
            newFeatureAttributes[FieldNames.featureFields.hasActual] = null;
            newFeatureAttributes[FieldNames.featureFields.hasGroup] = null;
            newFeatureAttributes[FieldNames.featureFields.groupCount] = 0;
            newFeatureAttributes[FieldNames.featureFields.estimatedAssetCost] = null;
            newFeatureAttributes[FieldNames.featureFields.estimatedContractualCost] = null;
            newFeatureAttributes[FieldNames.featureFields.estimatedGrantsFixedCost] = null;
            newFeatureAttributes[FieldNames.featureFields.estimatedOtherCost] = null;
            newFeatureAttributes[FieldNames.featureFields.estimatedPersonnelCost] = null;
            newFeatureAttributes[FieldNames.featureFields.estimatedTotalCost] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingSource] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingSourceProgram] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingSourceCategory] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingSourceSubCategory] = null;
            newFeatureAttributes[FieldNames.featureFields.fundingUnitType] = null;
            newFeatureAttributes[FieldNames.featureFields.isCarryover] = null;
            newFeatureAttributes[FieldNames.featureFields.createdBy] =
                userInfo[FieldNames.userFields.nifcUsername];
            newFeatureAttributes[FieldNames.featureFields.createdOnDate] = getSaveableDate(
                new Date()
            );
            newFeatureAttributes[FieldNames.featureFields.modifiedBy] =
                userInfo[FieldNames.userFields.nifcUsername];
            newFeatureAttributes[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(
                new Date()
            );
            const missingFieldsCount = getAllMissingFieldsCount(
                newFeatureAttributes,
                isTreatment,
                userInfo,
                false,
                null
            );
            newFeatureAttributes[FieldNames.featureFields.status] = getStatus(
                newFeatureAttributes,
                newFeatureAttributes[FieldNames.featureFields.hasActual],
                false,
                missingFieldsCount
            );
        }
        return newFeatureAttributes;
    };

    const onHandleCopyClicked = async () => {
        let newFeatureAttributes = {};
        if (isTreatment) {
        }
        dispatch(setShowGlobalLoader(true));
        let geoType = FieldNames.joinFeatureValues.polygon;
        if (editableFeature?.attributes) {
            geoType =
                editableFeature.attributes[FieldNames.featureFields.joinFeature] ??
                editableFeature.attributes[FieldNames.featureFields.joinFeature];
        }
        if (editableFeature?.geometry) {
            const geoAddResponse = await GeometryService.addGeometry(
                editableFeature?.geometry,
                geoType,
                userInfo[FieldNames.userFields.nifcUsername]
            );
            let geoID = -1;
            if (geoAddResponse?.addResults?.length > 0) {
                const geoObjectID = geoAddResponse?.addResults[0]?.objectId;
                geoID = await IDService.getGeometryID(geoObjectID, geoType);
                if (geoID) {
                    newFeatureAttributes = cloneFeature(
                        newFeatureAttributes,
                        editableFeature?.attributes,
                        isTreatment,
                        geoType,
                        geoID
                    );
                    if (isTreatment) {
                        dispatch(resetTreatmentValidationState());
                        setBaseTreatmentAttributes(_.cloneDeep(newFeatureAttributes));
                    } else {
                        dispatch(resetActivityValidationState());
                        setBaseActivityAttributes(_.cloneDeep(newFeatureAttributes));
                    }
                    FeatureService.copyFeatureAttributes(newFeatureAttributes).then(
                        async (copyResp) => {
                            if (copyResp.addResults[0].success) {
                                const entityStringValue =
                                    newFeatureAttributes[FieldNames.featureFields.entityType] ===
                                    FieldNames.internalEntityTypes.postFire
                                        ? 'pf'
                                        : 'f';
                                newFeatureAttributes[FieldNames.featureFields.objectID] =
                                    copyResp.addResults[0].objectId;
                                const classForMessage = isTreatment ? `Treatment` : `Activity`;
                                // We need to use the JoinID/JoinFeature for the AuditLog too
                                AuditLogService.addAuditLogEntry(
                                    copyResp.addResults[0],
                                    geoID,
                                    geoType,
                                    userInfo[FieldNames.userFields.nifcUsername],
                                    [
                                        `Feature was copied from ${classForMessage}: ${editableFeature.attributes[FieldNames.featureFields.id]}`,
                                    ]
                                ).then((auditResp) => {
                                    setSnackbarMessage(`${classForMessage} copied successfully.`);
                                    setSnackbarOpen(true);
                                    dispatch(setShowGlobalLoader(false));
                                    setMode(FieldNames.modes.edit);

                                    if (isTreatment) {
                                        navigate(
                                            `/${entityStringValue}/treatments/${copyResp.addResults[0].objectId}`,
                                            { isActual: false }
                                        );
                                    } else {
                                        navigate(
                                            `/${entityStringValue}/activities/${copyResp.addResults[0].objectId}`,
                                            { isActual: false }
                                        );
                                    }
                                });
                            }
                            const geometryType =
                                newFeatureAttributes[FieldNames.featureFields.joinFeature];
                            const isPostFire = isFeaturePostFire(newFeatureAttributes);
                            emitCustomEvent(Events.RefreshLayer, {
                                geo: editableFeature?.geometry,
                                geometryType: geometryType,
                                isPostFire: isPostFire,
                                featureClass: isTreatment
                                    ? FieldNames.classTypes.treatment
                                    : FieldNames.classTypes.activity,
                                attributes: newFeatureAttributes,
                                isProject: false,
                            });
                            emitCustomEvent(Events.ClearModifiedLayer);
                        }
                    );
                }
            }
        }
    };

    const transferFeatureType = (
        newFeatureAttributes,
        editableFeatureAttributes,
        isCreatingTreatment,
        isCurrentTreatment,
        geoType,
        geoID
    ) => {
        newFeatureAttributes[FieldNames.featureFields.joinFeature] = geoType;
        newFeatureAttributes[FieldNames.featureFields.joinID] = geoID;
        newFeatureAttributes[FieldNames.featureFields.isParentTreatment] = isCurrentTreatment
            ? FieldNames.yesNo.yes
            : FieldNames.yesNo.no;
        newFeatureAttributes[FieldNames.featureFields.parentID] =
            editableFeatureAttributes[FieldNames.featureFields.id];
        newFeatureAttributes[FieldNames.featureFields.class] = isCreatingTreatment
            ? FieldNames.classTypes.treatment
            : FieldNames.classTypes.activity;
        newFeatureAttributes[FieldNames.featureFields.localID] =
            editableFeatureAttributes[FieldNames.featureFields.localID];
        newFeatureAttributes[FieldNames.featureFields.name] =
            editableFeatureAttributes[FieldNames.featureFields.name];
        newFeatureAttributes[FieldNames.featureFields.fundingSource] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingSourceProgram] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingSourceCategory] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingSourceSubCategory] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingUnitType] = null;
        newFeatureAttributes[FieldNames.featureFields.unit] =
            editableFeatureAttributes[FieldNames.featureFields.unit];
        newFeatureAttributes[FieldNames.featureFields.region] =
            editableFeatureAttributes[FieldNames.featureFields.region];
        newFeatureAttributes[FieldNames.featureFields.agency] =
            editableFeatureAttributes[FieldNames.featureFields.agency];
        newFeatureAttributes[FieldNames.featureFields.department] =
            editableFeatureAttributes[FieldNames.featureFields.department];
        newFeatureAttributes[FieldNames.featureFields.congressionalDistrictNumber] =
            editableFeatureAttributes[FieldNames.featureFields.congressionalDistrictNumber];
        newFeatureAttributes[FieldNames.featureFields.tribeName] =
            editableFeatureAttributes[FieldNames.featureFields.tribeName];
        newFeatureAttributes[FieldNames.featureFields.county] =
            editableFeatureAttributes[FieldNames.featureFields.county];
        newFeatureAttributes[FieldNames.featureFields.state] =
            editableFeatureAttributes[FieldNames.featureFields.state];
        newFeatureAttributes[FieldNames.featureFields.latitude] =
            editableFeatureAttributes[FieldNames.featureFields.latitude];
        newFeatureAttributes[FieldNames.featureFields.longitude] =
            editableFeatureAttributes[FieldNames.featureFields.longitude];
        newFeatureAttributes[FieldNames.featureFields.calculatedArea] =
            editableFeatureAttributes[FieldNames.featureFields.calculatedArea];
        newFeatureAttributes[FieldNames.featureFields.priority] =
            editableFeatureAttributes[FieldNames.featureFields.priority];
        newFeatureAttributes[FieldNames.featureFields.feasibility] =
            editableFeatureAttributes[FieldNames.featureFields.feasibility];
        newFeatureAttributes[FieldNames.featureFields.isWUI] =
            editableFeatureAttributes[FieldNames.featureFields.isWUI];
        newFeatureAttributes[FieldNames.featureFields.isFunded] =
            editableFeatureAttributes[FieldNames.featureFields.isFunded];
        newFeatureAttributes[FieldNames.featureFields.isApproved] =
            editableFeatureAttributes[FieldNames.featureFields.isApproved];
        newFeatureAttributes[FieldNames.featureFields.initiationDate] =
            editableFeatureAttributes[FieldNames.featureFields.initiationDate];
        newFeatureAttributes[FieldNames.featureFields.initiationFiscalYear] =
            editableFeatureAttributes[FieldNames.featureFields.initiationFiscalYear];
        newFeatureAttributes[FieldNames.featureFields.initiationFiscalQuarter] =
            editableFeatureAttributes[FieldNames.featureFields.initiationFiscalQuarter];
        newFeatureAttributes[FieldNames.featureFields.completionDate] =
            editableFeatureAttributes[FieldNames.featureFields.completionDate];
        newFeatureAttributes[FieldNames.featureFields.completionFiscalYear] =
            editableFeatureAttributes[FieldNames.featureFields.completionFiscalYear];
        newFeatureAttributes[FieldNames.featureFields.completionFiscalQuarter] =
            editableFeatureAttributes[FieldNames.featureFields.completionFiscalQuarter];
        newFeatureAttributes[FieldNames.featureFields.estimatedPersonnelCost] = null;
        newFeatureAttributes[FieldNames.featureFields.estimatedAssetCost] = null;
        newFeatureAttributes[FieldNames.featureFields.estimatedGrantsFixedCost] = null;
        newFeatureAttributes[FieldNames.featureFields.estimatedContractualCost] = null;
        newFeatureAttributes[FieldNames.featureFields.estimatedOtherCost] = null;
        newFeatureAttributes[FieldNames.featureFields.estimatedTotalCost] = null;
        newFeatureAttributes[FieldNames.featureFields.isBIL] = null;
        newFeatureAttributes[FieldNames.featureFields.bilFunding] =
            editableFeatureAttributes[FieldNames.featureFields.bilFunding];
        newFeatureAttributes[FieldNames.featureFields.notes] =
            editableFeatureAttributes[FieldNames.featureFields.notes];
        newFeatureAttributes[FieldNames.featureFields.localApprovalDate] = null;
        newFeatureAttributes[FieldNames.featureFields.regionalApprovalDate] = null;
        newFeatureAttributes[FieldNames.featureFields.agencyApprovalDate] = null;
        newFeatureAttributes[FieldNames.featureFields.departmentApprovalDate] = null;
        newFeatureAttributes[FieldNames.featureFields.unApprovalDate] = null;
        newFeatureAttributes[FieldNames.featureFields.unApprovalReason] = null;
        newFeatureAttributes[FieldNames.featureFields.fundedDate] =
            editableFeatureAttributes[FieldNames.featureFields.fundedDate];
        newFeatureAttributes[FieldNames.featureFields.statusReason] = null;
        newFeatureAttributes[FieldNames.featureFields.hasActual] = null;
        newFeatureAttributes[FieldNames.featureFields.hasGroup] = null;
        newFeatureAttributes[FieldNames.featureFields.groupCount] = 0;
        newFeatureAttributes[FieldNames.featureFields.createdBy] =
            userInfo[FieldNames.userFields.nifcUsername];
        newFeatureAttributes[FieldNames.featureFields.modifiedBy] =
            userInfo[FieldNames.userFields.nifcUsername];
        newFeatureAttributes[FieldNames.featureFields.createdOnDate] = getSaveableDate(new Date());
        newFeatureAttributes[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(
            new Date()
        );
        newFeatureAttributes[FieldNames.featureFields.vegDeparturePercentageDerived] = null;
        newFeatureAttributes[FieldNames.featureFields.vegDeparturePercentageManual] = null;
        newFeatureAttributes[FieldNames.featureFields.isVegetationManual] = FieldNames.yesNo.no;
        newFeatureAttributes[FieldNames.featureFields.fundingDepartment] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingAgency] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingRegion] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingUnit] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingSubUnit] = null;
        newFeatureAttributes[FieldNames.featureFields.fundingTribe] = null;
        newFeatureAttributes[FieldNames.featureFields.entityType] =
            editableFeatureAttributes[FieldNames.featureFields.entityType];
        newFeatureAttributes[FieldNames.isCarryover] = null;
        const missingFieldsCount = getAllMissingFieldsCount(
            newFeatureAttributes,
            isTreatment,
            userInfo,
            false,
            null
        );
        newFeatureAttributes[FieldNames.featureFields.status] = getStatus(
            newFeatureAttributes,
            newFeatureAttributes[FieldNames.featureFields.hasActual],
            false,
            missingFieldsCount
        );
        return newFeatureAttributes;
    };

    const onHandleCopyAsClicked = async (isCurrentTreatment) => {
        let newFeatureAttributes = {};
        dispatch(setShowGlobalLoader(true));
        let geoType = FieldNames.joinFeatureValues.polygon;
        if (editableFeature?.attributes) {
            geoType =
                editableFeature.attributes[FieldNames.featureFields.joinFeature] ??
                editableFeature.attributes[FieldNames.featureFields.joinFeature];
            const treatmentOrActivity = isCurrentTreatment
                ? FieldNames.classTypes.treatment
                : FieldNames.classTypes.activity;
            if (!checkIfCopyAllowed(editableFeature)) {
                const msg = `Copying a ${geoType} to this ${treatmentOrActivity} is not allowed.`;
                dispatch(setShowGlobalLoader(false));
                setWarningSeverity('warning');
                setSnackbarMessage(msg);
                setSnackbarOpen(true);
                throw Error(msg);
            }
        }
        // Check if geometry is allowed for the class of editableFeature. e.g. if editableFeature is a postfire 'estimated treatment', block process if copying a polyline as activity
        if (editableFeature?.geometry) {
            const geoAddResponse = await GeometryService.addGeometry(
                editableFeature?.geometry,
                geoType,
                userInfo[FieldNames.userFields.nifcUsername]
            );
            let geoID = -1;
            if (geoAddResponse?.addResults?.length > 0) {
                const geoObjectID = geoAddResponse?.addResults[0]?.objectId;
                geoID = await IDService.getGeometryID(geoObjectID, geoType);
                if (geoID) {
                    //Creates opposite of isCurrentTreatment
                    newFeatureAttributes = transferFeatureType(
                        newFeatureAttributes,
                        editableFeature?.attributes,
                        !isCurrentTreatment,
                        isCurrentTreatment,
                        geoType,
                        geoID
                    );
                    const isPostFire = isFeaturePostFire(newFeatureAttributes);
                    FeatureService.copyFeatureAttributes(newFeatureAttributes).then(
                        async (response) => {
                            if (response.addResults[0].success) {
                                const classForMessage = isCurrentTreatment
                                    ? `Treatment`
                                    : `Activity`;
                                const classToMessage = !isCurrentTreatment
                                    ? `Treatment`
                                    : `Activity`;
                                AuditLogService.addAuditLogEntry(
                                    response.addResults[0],
                                    geoID,
                                    geoType,
                                    userInfo[FieldNames.userFields.nifcUsername],
                                    [
                                        `Feature was copied from ${classForMessage}: ${editableFeature.attributes[FieldNames.featureFields.id]}`,
                                    ]
                                ).then((auditResp) => {
                                    setSnackbarMessage(
                                        `The ${classForMessage} was copied as a ${classToMessage} successfully.`
                                    );
                                    setSnackbarOpen(true);
                                    dispatch(setShowGlobalLoader(false));
                                    const entityStringValue = isPostFire ? 'pf' : 'f';
                                    if (!isCurrentTreatment) {
                                        navigate(
                                            `/${entityStringValue}/treatments/${response.addResults[0].objectId}`,
                                            { isActual: false }
                                        );
                                    } else {
                                        navigate(
                                            `/${entityStringValue}/activities/${response.addResults[0].objectId}`,
                                            { isActual: false }
                                        );
                                    }
                                    setMode(FieldNames.modes.edit);
                                    emitCustomEvent(Events.RefreshLayer, {
                                        geo: editableFeature?.geometry,
                                        geometryType: geoType,
                                        isPostFire: isPostFire,
                                        featureClass: !isCurrentTreatment
                                            ? FieldNames.classTypes.treatment
                                            : FieldNames.classTypes.activity,
                                        attributes: newFeatureAttributes,
                                        isProject: false,
                                    });
                                    emitCustomEvent(Events.ClearModifiedLayer);
                                });
                            }
                        }
                    );
                }
            }
        }
    };

    const checkIfCopyAllowed = (existingFeature) => {
        // Return false if we are copying a polyline postfire treatment (Estimated Treatment) as a postfire activity (Estimated Activity)
        // This is currently the only case where an invalid geometry might be copied.
        const geoType = existingFeature?.attributes[FieldNames.featureFields.joinFeature];
        const entityClass = existingFeature?.attributes?.Class;
        return !(geoType === 'Line' && entityClass === 'Estimated Treatment');
    };

    const onSaveChangesHelper = (tempEditableFeature, isNew, fields) => {
        if (isNew) {
            if (userInfo) {
                tempEditableFeature.attributes[fields.createdBy] =
                    userInfo[FieldNames.userFields.nifcUsername];
                tempEditableFeature.attributes[fields.createdOnDate] = getSaveableDate(new Date());
            }
        }
        return tempEditableFeature;
    };

    const onSaveChanges = async () => {
        try {
            dispatch(setShowGlobalLoader(true));
            setIsSaveDisabled(true);
            let fields = FieldNames.featureFields;
            let actualFields = FieldNames.featureFields;
            let tempEditableFeature = editableFeature;

            if (tempEditableFeature?.attributes) {
                if (doClearCancelLogic) {
                    const cancelFeature = await CancelService.getFeatureCancelReason(
                        tempEditableFeature.attributes[FieldNames.featureFields.joinFeature],
                        tempEditableFeature.attributes[FieldNames.featureFields.joinID]
                    );
                    if (cancelFeature) {
                        let cancelObject = {
                            OBJECTID: cancelFeature[FieldNames.featureFields.objectID],
                            ModifiedBy: userInfo[FieldNames.userFields.nifcUsername],
                            ModifiedDate: getSaveableDate(new Date()),
                            IsArchived: FieldNames.yesNo.yes,
                        };

                        const updateCancelResp =
                            await CancelService.updateCanceledFeatureEntry(cancelObject);
                        setDoClearCancelLogic(false);
                    }
                }

                //These items are the same whether it's a new feature or we are just updating a feature
                let perimeter = tempEditableFeature.geometry;
                if (!perimeter) {
                    perimeter = tempEditableFeature.perimeter;
                }

                if (!tempEditableFeature.attributes[fields.initiationFiscalYear]) {
                    tempEditableFeature.attributes[fields.initiationFiscalYear] = null;
                }
                if (!tempEditableFeature.attributes[fields.completionFiscalYear]) {
                    tempEditableFeature.attributes[fields.completionFiscalYear] = null;
                }
                if (!tempEditableFeature.attributes[fields.cancelledDate]) {
                    tempEditableFeature.attributes[fields.cancelledDate] = null;
                }
                if (!tempEditableFeature.attributes[fields.durability]) {
                    tempEditableFeature.attributes[fields.durability] = null;
                }
                if (userInfo) {
                    tempEditableFeature.attributes[fields.modifiedBy] =
                        userInfo[FieldNames.userFields.nifcUsername];
                    tempEditableFeature.attributes[fields.lastModifiedDate] = new Date().getTime();
                    tempEditableFeature.attributes['LastModifiedBy'] =
                        userInfo[FieldNames.userFields.nifcUsername];
                }

                tempEditableFeature = onSaveChangesHelper(
                    tempEditableFeature,
                    isNewFeature,
                    fields
                );
                if (tempEditableFeature) {
                    tempEditableFeature = buildCostCode(null, tempEditableFeature, fields, null);

                    //Differences based on new vs update
                    if (
                        tempEditableFeature &&
                        isNewFeature
                    ) // We are creating a feature, not editing one
                    {
                        onSaveNewChanges(perimeter, tempEditableFeature);
                    } else if (
                        tempEditableFeature?.attributes
                    ) // We are Editing the Feature, not creating a new one
                    {
                        onSaveUpdateChanges(perimeter, tempEditableFeature, fields, actualFields);
                    }
                }
            }
        } catch (e) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], e);
        }
    };

    const processInteragencyValues = async (geoID, geoType) => {
        const otherCheckbox = interagencySelected.filter(
            (X) =>
                X[FieldNames.interagencyFields.interagencyName] ===
                FieldNames.interagencyValues.other
        );
        if (
            otherInteragencySelected &&
            otherInteragencySelected.length > 0 &&
            otherCheckbox &&
            otherCheckbox.length > 0
        ) {
            otherInteragencySelected.forEach((val) => {
                if (!val.InteragencyName.includes(FieldNames.interagencyValues.otherShortened)) {
                    val.InteragencyName = `${FieldNames.interagencyValues.otherShortened}${val.InteragencyName}`;
                }
                interagencySelected.push(val);
            });
        }
        // Update only applies for Other
        const updatedInteragencyValues = otherInteragencySelected
            .filter((x) => x[FieldNames.interagencyFields.objectID] !== null && x.IsChanged)
            ?.map((x) => {
                if (!x[FieldNames.interagencyFields.joinID]) {
                    x[FieldNames.interagencyFields.joinID] = geoID;
                }
                if (!x[FieldNames.interagencyFields.joinFeature]) {
                    x[FieldNames.interagencyFields.joinFeature] = geoType;
                }
                return x;
            });

        const newInteragencyValues = interagencySelected
            .filter((x) => x[FieldNames.interagencyFields.objectID] === null)
            ?.map((x) => {
                if (!x[FieldNames.interagencyFields.joinID]) {
                    x[FieldNames.interagencyFields.joinID] = geoID;
                }
                if (!x[FieldNames.interagencyFields.joinFeature]) {
                    x[FieldNames.interagencyFields.joinFeature] = geoType;
                }
                return x;
            });

        let updatedInteragencySupport = null;
        let newInteragencySuppport = null;

        if (updatedInteragencyValues && updatedInteragencyValues.length > 0) {
            updatedInteragencySupport =
                await InteragencyService.updateMultipleInteragency(updatedInteragencyValues);
        }

        if (newInteragencyValues && newInteragencyValues.length > 0) {
            newInteragencySuppport =
                await InteragencyService.addMultipleInteragency(newInteragencyValues);
        }

        const updatedItemsObjectIDs = [];
        const finalItemsToDelete = itemsToDelete?.filter(
            (x) =>
                !updatedItemsObjectIDs.includes(x[FieldNames.interagencyFields.objectID]) &&
                x[FieldNames.interagencyFields.objectID]
        );

        if (finalItemsToDelete && finalItemsToDelete.length > 0) {
            const deleteResponse =
                await InteragencyService.deleteMultipleInteragency(finalItemsToDelete);
        }

        setInteragencyRefresh(true);
        return {
            updated: updatedInteragencySupport,
            new: newInteragencySuppport,
            delete: finalItemsToDelete,
        };
    };

    const onSaveNewChanges = async (perimeter, tempEditableFeature) => {
        try {
            if (
                tempEditableFeature?.attributes &&
                isNewFeature
            ) // We are creating a feature, not editing one
            {
                const geoType =
                    tempEditableFeature.attributes[FieldNames.featureFields.joinFeature] ??
                    FieldNames.joinFeatureValues.polygon; // Failsafe
                const geoAddResponse = await GeometryService.addGeometry(
                    perimeter,
                    geoType,
                    userInfo[FieldNames.userFields.nifcUsername]
                );
                if (geoAddResponse?.addResults?.length > 0) {
                    const geoObjectID = geoAddResponse?.addResults[0]?.objectId;
                    const geoID = await IDService.getGeometryID(geoObjectID, geoType);

                    tempEditableFeature.attributes[FieldNames.featureFields.joinID] = geoID;
                    tempEditableFeature.attributes[FieldNames.featureFields.joinFeature] = geoType;
                    const interagencyResponse = await processInteragencyValues(geoID, geoType);
                    if (interagencyResponse?.newInteragencySuppport?.successful) {
                        InteragencyService.getAllInteragencyByJoinIDFeature(geoID, geoType).then(
                            (interagencyData) => {
                                setInteragencySelected(interagencyData);
                            }
                        );
                    }
                    const response = await FeatureService.addFeature(
                        tempEditableFeature.attributes
                    );
                    tempEditableFeature.attributes[FieldNames.featureFields.joinObjectID] =
                        geoObjectID;
                    const isPostFire = isFeaturePostFire(tempEditableFeature.attributes);
                    setSnackbarOpen(true);
                    emitCustomEvent(Events.DisableDrawMode);
                    if (
                        isPostFire &&
                        (tempEditableFeature?.attributes[FieldNames.featureFields.unitOfMeasure] ||
                            tempEditableFeature?.attributes[FieldNames.featureFields.measureAmount])
                    ) {
                        const measurementResponse = await addMeasurementEntry(
                            tempEditableFeature,
                            geoType,
                            geoID,
                            true,
                            userInfo
                        );
                        if (measurementResponse?.addResults[0].objectId) {
                            tempEditableFeature.attributes[
                                FieldNames.featureFields.measurementOBJECTID
                            ] = measurementResponse?.addResults[0].objectId;
                        }
                    }

                    const auditResponse = await addFeatureAuditLogEntry(
                        tempEditableFeature,
                        geoType,
                        geoID,
                        true,
                        true
                    ).catch((error) => {
                        ErrorService.addErrorLogEntry(
                            userInfo[FieldNames.userFields.nifcUsername],
                            error
                        );
                    });

                    setIsSaveDisabled(false);
                    dispatch(setShowGlobalLoader(false));
                    setFeature(tempEditableFeature);
                    setEditableFeature(tempEditableFeature);
                    setIsNewFeature(false);
                    setSnackbarMessage(
                        isTreatment ? 'Added new Treatment.' : 'Added new Activity.'
                    );
                    const entityStringValue = isPostFire ? 'pf' : 'f';
                    if (isTreatment) {
                        setBaseTreatmentAttributes(null);
                        navigate(
                            `/${entityStringValue}/treatments/${response.addResults[0].objectId}`,
                            { isActual: false }
                        );
                    } else {
                        setBaseActivityAttributes(null);
                        navigate(
                            `/${entityStringValue}/activities/${response.addResults[0].objectId}`,
                            { isActual: false }
                        );
                    }
                    emitCustomEvent(Events.RefreshLayer, {
                        geo: perimeter,
                        geometryType: geoType,
                        isPostFire: isPostFire,
                        featureClass: isTreatment
                            ? FieldNames.classTypes.treatment
                            : FieldNames.classTypes.activity,
                        attributes: tempEditableFeature.attributes,
                        isProject: false,
                    });
                    emitCustomEvent(Events.ClearModifiedLayer);
                }
            }
        } catch (e) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], e);
        }
    };

    const onSaveUpdateChanges = async (perimeter, tempEditableFeature, fields, actualFields) => {
        try {
            if (
                tempEditableFeature?.attributes
            ) // We are Editing the Feature, not creating a new one
            {
                const geoType =
                    tempEditableFeature.attributes[FieldNames.featureFields.joinFeature] ??
                    FieldNames.joinFeatureValues.polygon; // Failsafe
                const geoID = tempEditableFeature?.attributes
                    ? tempEditableFeature?.attributes[FieldNames.featureFields.joinID]
                    : null;
                const geoUpdateResponse = await GeometryService.updateGeometry(
                    perimeter,
                    geoType,
                    userInfo[FieldNames.userFields.nifcUsername],
                    tempEditableFeature.attributes[FieldNames.featureFields.joinObjectID]
                );
                const response = await FeatureService.updateFeature(tempEditableFeature);
                const isPostFire = isFeaturePostFire(tempEditableFeature.attributes);
                if (geoType && geoID) {
                    const interagencyResponse = await processInteragencyValues(geoID, geoType);
                    if (interagencyResponse?.newInteragencySuppport?.successful) {
                        InteragencyService.getAllInteragencyByJoinIDFeature(geoID, geoType).then(
                            (interagencyData) => {
                                setInteragencySelected(interagencyData);
                            }
                        );
                    }
                }
                let measurementResponse = null;
                if (tempEditableFeature.attributes[FieldNames.featureFields.measurementOBJECTID]) {
                    if (
                        isPostFire &&
                        (tempEditableFeature?.attributes[FieldNames.featureFields.unitOfMeasure] ||
                            tempEditableFeature?.attributes[FieldNames.featureFields.measureAmount])
                    ) {
                        measurementResponse = await addMeasurementEntry(
                            tempEditableFeature,
                            geoType,
                            geoID,
                            false,
                            userInfo
                        );
                    }
                } else {
                    if (
                        isPostFire &&
                        (tempEditableFeature?.attributes[FieldNames.featureFields.unitOfMeasure] ||
                            tempEditableFeature?.attributes[FieldNames.featureFields.measureAmount])
                    ) {
                        measurementResponse = await addMeasurementEntry(
                            tempEditableFeature,
                            geoType,
                            geoID,
                            true,
                            userInfo
                        );
                        if (measurementResponse?.addResults[0].objectId) {
                            tempEditableFeature.attributes[
                                FieldNames.featureFields.measurementOBJECTID
                            ] = measurementResponse?.addResults[0].objectId;
                        }
                    }
                }

                const auditResp = await addFeatureAuditLogEntry(
                    tempEditableFeature,
                    tempEditableFeature.attributes[FieldNames.featureFields.joinFeature],
                    tempEditableFeature.attributes[FieldNames.featureFields.joinID],
                    false,
                    true
                ).catch((error) => {
                    ErrorService.addErrorLogEntry(
                        userInfo[FieldNames.userFields.nifcUsername],
                        error
                    );
                });

                emitCustomEvent(Events.DisableDrawMode);
                setSnackbarOpen(true);
                setIsSaveDisabled(false);
                dispatch(setShowGlobalLoader(false));
                setSnackbarMessage(
                    isTreatment
                        ? 'Treatment changes saved successfully.'
                        : 'Activity changes saved successfully.'
                );
                if (isTreatment) {
                    dispatch(resetTreatmentValidationState());
                    setBaseTreatmentAttributes(_.cloneDeep(tempEditableFeature.attributes));
                } else {
                    dispatch(resetActivityValidationState());
                    setBaseActivityAttributes(_.cloneDeep(tempEditableFeature.attributes));
                }
                setEditableFeature(tempEditableFeature);
                setFeature(tempEditableFeature);
                emitCustomEvent(Events.RefreshLayer, {
                    geo: perimeter,
                    geometryType: geoType,
                    isPostFire: isPostFire,
                    featureClass: isTreatment
                        ? FieldNames.classTypes.treatment
                        : FieldNames.classTypes.activity,
                    attributes: tempEditableFeature.attributes,
                    isProject: false,
                });
                emitCustomEvent(Events.ClearModifiedLayer);
            }
        } catch (e) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], e);
        }
    };

    const buildCostCode = (actualsObject, editableFeature, fields, actualFields) => {
        if (!actualsObject) {
            let wbsCode = editableFeature.attributes[fields.wbsID];
            if (!wbsCode) {
                wbsCode = 'XXXXXXXXXXXX';
                editableFeature.attributes[fields.wbsID] = wbsCode;
            }

            let functionalArea = editableFeature.attributes[fields.functionalArea];
            if (!functionalArea) {
                functionalArea = 'XXXXXXXXX.XXXXXX';
                editableFeature.attributes[fields.functionalArea] = functionalArea;
            }

            let costCenter = editableFeature.attributes[fields.costCenter];
            if (!costCenter) {
                costCenter = 'XXXXXXXXXX';
                editableFeature.attributes[fields.costCenter] = costCenter;
            }

            editableFeature.attributes[fields.costCode] =
                `${costCenter}/${functionalArea}/${wbsCode}`;

            let contributedFunctionalArea =
                editableFeature.attributes[fields.contributedFunctionalArea];
            if (!contributedFunctionalArea) {
                contributedFunctionalArea = 'XXXXXXXXX.XXXXXX';
                editableFeature.attributes[fields.contributedFunctionalArea] =
                    contributedFunctionalArea;
            }

            let contributedCostCenter = editableFeature.attributes[fields.contributedCostCenter];
            if (!contributedCostCenter) {
                contributedCostCenter = 'XXXXXXXXXX';
                editableFeature.attributes[fields.contributedCostCenter] = contributedCostCenter;
            }

            editableFeature.attributes[fields.contributedCostCode] =
                `${contributedCostCenter}/${contributedFunctionalArea}/XXXXXXXXXXXX`;
            return editableFeature;
        } else {
            let wbsCodeForActual = actualsObject.attributes[actualFields.wbsID];
            if (!wbsCodeForActual) {
                wbsCodeForActual = 'XXXXXXXXXXXX';
                actualsObject.attributes[actualFields.wbsID] = wbsCodeForActual;
            }

            let functionalAreaForActual = editableFeature.attributes[fields.functionalArea];
            if (!functionalAreaForActual) {
                functionalAreaForActual = 'XXXXXXXXX.XXXXXX';
            }
            actualsObject.attributes[actualFields.functionalArea] = functionalAreaForActual;

            let costCenterForActual = editableFeature.attributes[fields.costCenter];
            if (!costCenterForActual) {
                costCenterForActual = 'XXXXXXXXXX';
            }
            actualsObject.attributes[actualFields.costCenter] = costCenterForActual;
            actualsObject.attributes[actualFields.costCode] =
                `${costCenterForActual}/${functionalAreaForActual}/${wbsCodeForActual}`;

            let contributedFunctionalAreaForActual =
                editableFeature.attributes[fields.contributedFunctionalArea];
            if (!contributedFunctionalAreaForActual) {
                contributedFunctionalAreaForActual = 'XXXXXXXXX.XXXXXX';
            }
            actualsObject.attributes[actualFields.contributedFunctionalArea] =
                contributedFunctionalAreaForActual;

            let contributedCostCenterForActual =
                editableFeature.attributes[fields.contributedCostCenter];
            if (!contributedCostCenterForActual) {
                contributedCostCenterForActual = 'XXXXXXXXXX';
            }
            actualsObject.attributes[actualFields.contributedCostCenter] =
                contributedCostCenterForActual;
            actualsObject.attributes[actualFields.contributedCostCode] =
                `${contributedCostCenterForActual}/${contributedFunctionalAreaForActual}/XXXXXXXXXXXX`;

            return actualsObject;
        }
    };

    const onClose = () => {
        setMode(FieldNames.modes.read);
        emitCustomEvent(Events.DisableDrawMode);
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
        setFeature(null);
        navigate('/home');
    };

    const onManageCosts = () => {
        setCostFundingPopoverOpen(true);
    };

    const onCloseManageCosts = () => {
        setCostFundingPopoverOpen(false);
        if (
            editableFeature?.attributes?.[FieldNames.featureFields.joinID] &&
            editableFeature?.attributes?.[FieldNames.featureFields.joinFeature]
        ) {
            getFeatureAuditLog(
                editableFeature?.attributes?.[FieldNames.featureFields.joinID],
                editableFeature?.attributes?.[FieldNames.featureFields.joinFeature]
            );
        }
    };

    const closeCancelEditorCallback = () => {
        // Nothing necessary at this time
    };

    const saveCancelEditorCallback = (feature) => {
        if (feature) {
            setFeature(feature);
            setEditableFeature(feature);
            if (isTreatment) {
                setBaseTreatmentAttributes(esriLang.clone(feature?.attributes));
            } else {
                setBaseActivityAttributes(esriLang.clone(feature?.attributes));
            }
            getFeatureAuditLog(
                feature[FieldNames.featureFields.joinID],
                feature[FieldNames.featureFields.joinFeature]
            );
        }
    };

    const createActual = () => {
        emitCustomEvent(Events.SetActualIsNew, true);
        setIsCreatingActualFeature(true);
        setIsCreatingPlannedFeature(false);
        setSelectedFeatures([editableFeature.attributes]);
        setPlannedFeature(editableFeature);
        dispatch(setIsLocationFilterOnly(false));
        dispatch(setFilterGeometry(null));
    };

    // finalize by marking complete and setting date
    const finalizeProgressiveReporting = () => {
        editableFeature.attributes[FieldNames.featureFields.status] = FieldNames.statuses.completed;
        const now = new Date();
        editableFeature.attributes[FieldNames.featureFields.completionDate] = getSaveableDate(now);
        editableFeature.attributes[FieldNames.featureFields.completionFiscalYear] =
            calculateFiscalYearQuarter(now, true);
        editableFeature.attributes[FieldNames.featureFields.completionFiscalQuarter] =
            calculateFiscalYearQuarter(now, false);
        // save
        onSaveChanges();
    };
    // unfinalize by removing going back to approved or started and clearing date
    const unfinalizeProgressiveReporting = () => {
        // if there are actuals, set to Started, else set to Approved
        const hasActuals = editableFeature.attributes[FieldNames.featureFields.hasActual];
        editableFeature.attributes[FieldNames.featureFields.status] = getStatus(
            editableFeature.attributes,
            hasActuals,
            false,
            0
        );
        editableFeature.attributes[FieldNames.featureFields.completionDate] = null;
        editableFeature.attributes[FieldNames.featureFields.completionFiscalQuarter] = null;
        editableFeature.attributes[FieldNames.featureFields.completionFiscalYear] = null;

        // save
        onSaveChanges();
    };

    const reportActual = () => {
        const entityStringValue = isFeaturePostFire(actualsObject) ? 'pf' : 'f';
        if (isTreatment) {
            if (actualsObject && actualsObject.attributes) {
                navigate(
                    `/${entityStringValue}/treatments/${actualsObject.attributes[FieldNames.featureFields.objectID]}`,
                    { isActual: true }
                );
            }
        } else {
            if (actualsObject && actualsObject.attributes) {
                navigate(
                    `/${entityStringValue}/activities/${actualsObject.attributes[FieldNames.featureFields.objectID]}`,
                    { isActual: true }
                );
            }
        }
    };

    const actualsText = (detail) => {
        if (
            detail.identifier === 'proposalPanel' &&
            categoryValue !== FieldNames.categoriesForChecking.programManagement &&
            feature.attributes[FieldNames.featureFields.isProgressiveReporting] !==
                FieldNames.yesNo.yes
        ) {
            return (
                <Fragment>
                    <Grid
                        item
                        size={12}
                        container
                        direction='row'
                        className='grid-item'
                        style={{ alignItems: 'center', maxWidth: '550px', paddingRight: '5px' }}
                    >
                        {!hasActual && !actualsObject && (
                            <Grid item size={3}>
                                <Chip
                                    disabled={!isApprovedAndFunded}
                                    className='required-field-status-chip'
                                    onClick={() => createActual()}
                                    style={{
                                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                        marginBottom: 5,
                                        marginTop: 5,
                                        zIndex: 5,
                                    }}
                                    avatar={
                                        <Avatar
                                            style={{
                                                fontSize: '12px',
                                                width: '22px',
                                                height: '22px',
                                                backgroundColor: ColorConstants.AvatarColor,
                                                color: ColorConstants.WhiteAlien,
                                            }}
                                        >
                                            <AddIcon />
                                        </Avatar>
                                    }
                                    label={
                                        <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                            Create Actual
                                        </span>
                                    }
                                />
                            </Grid>
                        )}
                        {hasActual && actualsObject && (
                            <Grid item size={3}>
                                <Chip
                                    className='required-field-status-chip'
                                    onClick={() => reportActual()}
                                    style={{
                                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                        marginBottom: 5,
                                        marginTop: 5,
                                        zIndex: 5,
                                    }}
                                    avatar={
                                        <Avatar
                                            style={{
                                                fontSize: '12px',
                                                width: '22px',
                                                height: '22px',
                                                backgroundColor: ColorConstants.AvatarColor,
                                                color: ColorConstants.WhiteAlien,
                                            }}
                                        >
                                            <SearchIcon style={{ width: '18px', height: '18px' }} />
                                        </Avatar>
                                    }
                                    label={
                                        <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                            Report on Actual
                                        </span>
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </Fragment>
            );
        } else {
            return '';
        }
    };

    const navigateToProject = async () => {
        const entityStringValue = isFeaturePostFire(editableFeature) ? 'pf' : 'f';
        if (editableFeature?.attributes?.[FieldNames.featureFields.projectID]) {
            const projectObjectID = await ProjectService.getProjectObjectIDByID(
                editableFeature?.attributes?.[FieldNames.featureFields.projectID]
            );
            navigate(`/${entityStringValue}/projects/${projectObjectID?.attributes?.OBJECTID}`);
        }
    };

    const getProjectChip = (detail) => {
        if (detail.identifier === 'projectPanel') {
            return (
                <Fragment>
                    <Grid
                        item
                        size={12}
                        container
                        direction='row'
                        className='grid-item'
                        style={{ alignItems: 'center', maxWidth: '550px', paddingRight: '5px' }}
                    >
                        <Tooltip
                            title={
                                editableFeature?.attributes?.[FieldNames.featureFields.projectID]
                                    ? ''
                                    : 'There is no Project related to this action'
                            }
                        >
                            <Grid item size={3}>
                                <Chip
                                    disabled={
                                        !editableFeature?.attributes?.[
                                            FieldNames.featureFields.projectID
                                        ]
                                    }
                                    className='required-field-status-chip'
                                    onClick={() => navigateToProject()}
                                    style={{
                                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                        marginBottom: 5,
                                        marginTop: 5,
                                        zIndex: 5,
                                    }}
                                    avatar={
                                        <Avatar
                                            style={{
                                                fontSize: '12px',
                                                width: '22px',
                                                height: '22px',
                                                backgroundColor: ColorConstants.AvatarColor,
                                                color: ColorConstants.WhiteAlien,
                                            }}
                                        >
                                            <AddIcon />
                                        </Avatar>
                                    }
                                    label={
                                        <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                            Return to Project
                                        </span>
                                    }
                                />
                            </Grid>
                        </Tooltip>
                    </Grid>
                </Fragment>
            );
        } else {
            return '';
        }
    };

    const contentsByTab = (detail) => {
        let updatedDetail = JSON.parse(JSON.stringify(detail));
        if (editableFeature?.attributes[FieldNames.featureFields.fundingAgency]) {
            if (
                !editableFeature?.attributes[FieldNames.featureFields.fundingAgency]?.includes(
                    FieldNames.agencies.blm
                )
            ) {
                //The fields are removed from the screen unless the current agency contains blm
                updatedDetail.fields = updatedDetail.fields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.fundingSubUnit &&
                        x !== FieldNames.featureFields.treatmentDriver
                );
            }

            if (
                !editableFeature?.attributes[FieldNames.featureFields.fundingAgency]?.includes(
                    FieldNames.agencies.bia
                )
            ) {
                //The fields are removed from the screen unless the current agency contains bia
                updatedDetail.fields = updatedDetail.fields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.isRTRL &&
                        x !== FieldNames.featureFields.fundingTribe
                );
            }
        } else {
            const subTypePossibleValues = getByLevel(
                editableFeature?.attributes,
                FieldNames.categoryAssociationFields.subTypes
            );
            if (!subTypePossibleValues || subTypePossibleValues?.length === 0) {
                updatedDetail.fields = updatedDetail?.fields?.filter(
                    (x) => x !== FieldNames.featureFields.subType
                );
            }
            updatedDetail.fields = updatedDetail.fields.filter(
                (x) =>
                    x !== FieldNames.featureFields.fundingSubUnit &&
                    x !== FieldNames.featureFields.treatmentDriver &&
                    x !== FieldNames.featureFields.isRTRL &&
                    x !== FieldNames.featureFields.fundingTribe
            );
        }

        if (isFeaturePostFire(editableFeature?.attributes)) {
            updatedDetail.fields = updatedDetail.fields.filter((field) => {
                return (
                    field !== FieldNames.featureFields.isWUI &&
                    field !== FieldNames.featureFields.isVegetationManual &&
                    field !== FieldNames.featureFields.vegDeparturePercentageDerived &&
                    field !== FieldNames.featureFields.vegDeparturePercentageManual &&
                    field !== FieldNames.featureFields.durability &&
                    field !== FieldNames.featureFields.isRTRL
                );
            });
        }

        if (
            editableFeature?.attributes[FieldNames.featureFields.joinFeature] ===
                FieldNames.joinFeatureValues.point ||
            editableFeature?.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
        ) {
            updatedDetail.fields = updatedDetail.fields.filter(
                (field) =>
                    field !== FieldNames.featureFields.calculatedArea &&
                    field !== FieldNames.featureFields.areaUnit
            );
        }

        return (
            <div className='details-panel'>
                {updatedDetail.identifier === FieldNames.panels.proposal && (
                    <AssociateChips
                        userInfo={userInfo}
                        detailIdentifier={updatedDetail.identifier}
                        feature={feature}
                        editableFeature={editableFeature}
                        setAssociatedCount={setAssociatedCount}
                        featureIsNew={isNewFeature}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isCurrentTreatment={isTreatment}
                        isCurrentActual={false}
                        associatedCount={associatedCount}
                        setEditableFeature={setEditableFeature}
                        featureGroups={featureGroups}
                        setFeatureGroups={setFeatureGroups}
                    />
                )}
                {updatedDetail.identifier === FieldNames.panels.progressive && (
                    <ProgressiveReportingList
                        joinId={feature.attributes[FieldNames.featureFields.joinID]}
                        joinFeature={feature.attributes[FieldNames.featureFields.joinFeature]}
                        status={feature.attributes[FieldNames.featureFields.status]}
                        isApprovedAndFunded={isApprovedAndFunded}
                        onAddActualClicked={createActual}
                        onFinalizeClicked={finalizeProgressiveReporting}
                        onUnfinalizeClicked={unfinalizeProgressiveReporting}
                    ></ProgressiveReportingList>
                )}
                {actualsText(updatedDetail)}
                {updatedDetail.fields.map((field, i) =>
                    field && field.substring(0, 3) === '*H*' ? (
                        <div key={`${field} - ${i} - Subheader`}>
                            <Typography variant='h5' sx={{ textDecoration: 'underline' }}>
                                {field.substring(3)}
                            </Typography>
                        </div>
                    ) : (
                        <DetailsGridItem
                            field={field}
                            key={`${field} - ${i} - Field`}
                            mode={mode}
                            treatment={isTreatment ? editableFeature : null}
                            activity={!isTreatment ? editableFeature : null}
                            onFieldChange={onHandleAttributeChanged}
                            getAllMissingFieldsCount={getAllMissingFieldsCount}
                            getAllApprovalFieldsCount={getAllApprovalFieldsCount}
                            setMode={setMode}
                            classValue={classValue}
                            setClassValue={setClassValue}
                            categoryValue={categoryValue}
                            setCategoryValue={setCategoryValue}
                            isTreatment={isTreatment}
                            userInfo={userInfo}
                            userRoleInfo={userRoleInfo}
                            locationDepartmentValue={locationDepartmentValue}
                            setLocationDepartmentValue={setLocationDepartmentValue}
                            locationAgencyValue={locationAgencyValue}
                            setLocationAgencyValue={setLocationAgencyValue}
                            locationRegionValue={locationRegionValue}
                            setLocationRegionValue={setLocationRegionValue}
                            locationUnitValue={locationUnitValue}
                            setLocationUnitValue={setLocationUnitValue}
                            tribeNameValue={tribeNameValue}
                            setTribeNameValue={setTribeNameValue}
                            isNew={isNewFeature}
                            fundingSourceOptions={fundingSourceOptions}
                            isCancelled={isCancelled}
                            onFiscalFieldChange={onHandleDateWithFYFQChanged}
                            departureIndexOptions={departureIndexOptions}
                            contributedFundingSourceOptions={contributedFundingSourceOptions}
                            statusOptions={statusOptions}
                            requiredFields={requiredFields}
                            typeValue={typeValue}
                            setTypeValue={setTypeValue}
                            subTypeValue={subTypeValue}
                            setSubTypeValue={setSubTypeValue}
                            setIsUnApprovalRequested={setIsUnApprovalRequested}
                            setUnApprovalEditorOpen={setUnApprovalEditorOpen}
                            setUnApprovalDate={setUnApprovalDate}
                            setUnApprovalReason={setUnApprovalReason}
                            projectValue={projectValue}
                            setProjectValue={setProjectValue}
                            unitOfMeasureValue={unitOfMeasureValue}
                            setUnitOfMeasureValue={setUnitOfMeasureValue}
                            programValue={programValue}
                            setProgramValue={setProgramValue}
                            setCancelEditorOpen={setCancelEditorOpen}
                            interagencySelected={interagencySelected}
                            setInteragencySelected={setInteragencySelected}
                            otherInteragencySelected={otherInteragencySelected}
                            setOtherInteragencySelected={setOtherInteragencySelected}
                            itemsToDelete={itemsToDelete}
                            setItemsToDelete={setItemsToDelete}
                            setIsSaveDisabled={setIsSaveDisabled}
                        />
                    )
                )}
                {getProjectChip(updatedDetail)}
            </div>
        );
    };

    const getAllUnsavedFieldsCount = (feature) => {
        let count = 0;
        groupDetails?.map((detail) => (count += getUnsavedFieldsCountByGroup(feature, detail)));

        return count <= 0 ? 0 : count;
    };

    const getAllApprovalFieldsCount = (feature, isRequired) => {
        let approvals = false;
        if (
            feature &&
            feature.attributes &&
            feature.attributes[FieldNames.featureFields.modifiedBy] !== FieldNames.modifiedBy.nfpors
        ) {
            if (
                isRequired &&
                (feature.attributes[FieldNames.featureFields.localApprovalDate] ||
                    feature.attributes[FieldNames.featureFields.regionalApprovalDate] ||
                    feature.attributes[FieldNames.featureFields.agencyApprovalDate] ||
                    feature.attributes[FieldNames.featureFields.departmentApprovalDate])
            ) {
                approvals = true;
            }
        }
        return approvals;
    };

    const getMissingFieldsCountByGroup = (feature, detail) => {
        let count = 0;
        let remainingReqFields = [];
        if (feature?.attributes && detail) {
            const missingFields = getAllMissingFields(feature, isTreatment, userInfo, false, null);
            if (
                detail.fields &&
                missingFields &&
                detail.fields.length > 0 &&
                missingFields.length > 0
            ) {
                detail.fields.forEach((element) => {
                    if (missingFields.includes(element)) {
                        remainingReqFields.push(element);
                    }
                });
            } else {
                remainingReqFields = [];
            }
            count = remainingReqFields.length;
        }
        return count <= 0 ? 0 : count;
    };

    const getUnsavedFieldsCountByGroup = (feature, detail) => {
        let count = 0;
        let fieldsUnsavedInGroup = [];
        let unsavedFields = {};
        if (feature && detail) {
            if (isTreatment) {
                // Estimated Treatment
                unsavedFields = treatmentValidationUnsavedValues;
            } else {
                // Estimated Activity
                unsavedFields = activityValidationUnsavedValues;
            }

            for (let prop in unsavedFields) {
                if (unsavedFields[prop] === false) {
                    continue;
                } else {
                    if (detail && detail.fields && detail.fields.length > 0) {
                        if (detail.fields.includes(prop)) {
                            fieldsUnsavedInGroup.push(prop);
                        }
                    }
                }
            }
            count = fieldsUnsavedInGroup.length;
        }
        return count <= 0 ? 0 : count;
    };

    const isInGroup = (detail, item) => {
        let isInCurrentGroup = false;
        if (item && detail && item.attributes) {
            const requiredFields = getRequiredFields(
                item,
                isTreatment,
                editableFeature.attributes[FieldNames.featureFields.fundingAgency],
                userInfo,
                false
            );
            if (
                detail.fields &&
                requiredFields &&
                detail.fields.length > 0 &&
                requiredFields.length > 0
            ) {
                detail.fields.forEach((element) => {
                    if (requiredFields.includes(element)) {
                        isInCurrentGroup = true;
                    }
                });
            }
        }
        return isInCurrentGroup;
    };

    const chipClicked = () => {
        let groupings = FormGroupings.treatmentDetails;
        if (!isTreatment) {
            groupings = FormGroupings.activityDetails;
        }
        let itemsToOpen = [];
        let updatedValue = {};
        groupings.forEach((element) => {
            const count = getMissingFieldsCountByGroup(editableFeature, element);
            if (count > 0) {
                updatedValue[element.identifier] = 'true';
                itemsToOpen.push(element.identifier);
            } else {
                updatedValue[element.identifier] = 'false';
            }
        });

        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));
    };

    const chipClickedUnsaved = () => {
        let groupings = FormGroupings.treatmentDetails;
        if (!isTreatment) {
            groupings = FormGroupings.activityDetails;
        }
        let itemsToOpen = [];
        let updatedValue = {};
        groupings.forEach((element) => {
            const count = getUnsavedFieldsCountByGroup(editableFeature, element);
            if (count > 0) {
                updatedValue[element.identifier] = 'true';
                itemsToOpen.push(element.identifier);
            } else {
                updatedValue[element.identifier] = 'false';
            }
        });

        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));
    };

    const expandedChanged = (e, id) => {
        let currentItem = expandedItems[id];
        if (currentItem === 'true') {
            currentItem = 'false';
        } else {
            currentItem = 'true';
        }
        let updatedValue = {};
        updatedValue = { [id]: currentItem };
        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));
    };

    const getAccordionContent = (detail, feature) => {
        let isPoint = false;
        if (
            feature &&
            feature.attributes &&
            feature.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
        ) {
            isPoint = true;
        }
        if (
            detail?.identifier === FieldNames.panels.project &&
            feature?.attributes &&
            !isFeaturePostFire(feature.attributes)
        ) {
            return;
        }
        if (
            detail?.identifier === FieldNames.panels.progressive &&
            feature?.attributes[FieldNames.featureFields.isProgressiveReporting] !==
                FieldNames.yesNo.yes
        ) {
            return;
        }

        return (
            <div
                className={
                    detail.identifier === 'trackingPanel'
                        ? 'incident-details-content last'
                        : 'incident-details-content'
                }
                key={detail.identifier}
            >
                <Accordion
                    className='incident-details-panel'
                    expanded={expandedItems[detail.identifier] === 'true' ? true : false}
                    defaultExpanded={true}
                    style={{ marginTop: 10 }}
                >
                    <AccordionSummary
                        id={detail.identifier}
                        className='incident-details-summary'
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            expandedChanged(e, detail.identifier);
                        }}
                        style={{ zIndex: 10 }}
                        expandIcon={<ExpandMoreIcon />}
                    >
                        {title(detail)}
                        {isInGroup(detail, feature) &&
                        getUnsavedFieldsCountByGroup(feature, detail) > 0 &&
                        mode === FieldNames.modes.edit ? (
                            <DetailsEdited
                                unsavedFields={getUnsavedFieldsCountByGroup(feature, detail)}
                            />
                        ) : (
                            ''
                        )}
                        {isInGroup(detail, feature) &&
                        getMissingFieldsCountByGroup(feature, detail) > 0 &&
                        mode === FieldNames.modes.edit ? (
                            <DetailsRequired
                                missingFields={getMissingFieldsCountByGroup(feature, detail)}
                            />
                        ) : (
                            ''
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                        {detail.identifier === FieldNames.panels.location &&
                            mode === FieldNames.modes.edit && (
                                <Button
                                    disabled={isApproved || isNewFeature}
                                    color='primary'
                                    variant='contained'
                                    onClick={() =>
                                        rederiveFeatureCallback(
                                            feature,
                                            isTreatment,
                                            false,
                                            isPoint,
                                            null,
                                            null
                                        )
                                    }
                                >
                                    Derive
                                </Button>
                            )}
                        {detail && contentsByTab(detail)}
                        {detail.identifier === FieldNames.panels.tracking ? (
                            <Box sx={{ paddingTop: '10px !important' }}>
                                <Accordion>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls='panel1a-content'
                                        id='panel1a-header'
                                    >
                                        <Typography>View History</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box
                                            sx={{
                                                height: '250px',
                                                width: '100%',
                                                minWidth: '10px',
                                            }}
                                        >
                                            <DataGridPremium
                                                rows={featureHistoryRows}
                                                columns={featureHistoryCols}
                                            ></DataGridPremium>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        ) : null}
                    </AccordionDetails>
                </Accordion>
            </div>
        );
    };

    const getListPanel = () => {
        return (
            <Box
                className='incident-list-inner'
                style={{ height: '100%', overflowY: 'scroll', overflowX: 'hidden' }}
            >
                {groupDetails.map((detail) => getAccordionContent(detail, editableFeature))}
            </Box>
        );
    };

    //Is Approved being sent to the header is used to know if we should display the ability to delete. It only needs one approval for delete to be disabled
    return (
        <Box sx={{ display: 'flex' }}>
            <CancelEntryPopup
                currentUserInfo={userInfo}
                userRoleInfo={userRoleInfo}
                cancelEditorOpen={cancelEditorOpen}
                setCancelEditorOpen={setCancelEditorOpen}
                closeCallback={closeCancelEditorCallback}
                saveCallback={saveCancelEditorCallback}
                isBulkCancel={false}
                allBulkSelectedItems={null}
                feature={editableFeature}
                setEditableFeature={setEditableFeature}
            />
            <CssBaseline />
            <Drawer variant='permanent' open={true} headerheight={headerheight}>
                <div className='incidentList-panel'>
                    <DetailsHeader
                        feature={editableFeature}
                        getAllMissingFieldsCount={getAllMissingFieldsCount}
                        getAllUnsavedFieldsCount={getAllUnsavedFieldsCount}
                        closeDetail={onClose}
                        onEditClicked={onHandleEditClicked}
                        onCopyClicked={onHandleCopyClicked}
                        onCopyAsClicked={onHandleCopyAsClicked}
                        onDeleteClicked={onHandleDeleteClicked}
                        onSaveChanges={onSaveChanges}
                        mode={mode}
                        isNew={isNewFeature}
                        chipClicked={chipClicked}
                        chipClickedUnsaved={chipClickedUnsaved}
                        isTreatment={isTreatment}
                        hasActual={hasActual}
                        isApproved={isApproved}
                        isSaveDisabled={isSaveDisabled}
                        setIsSaveDisabled={setIsSaveDisabled}
                        disableEdit={disableEdit}
                        disableEditMessage={disableEditMessage}
                        disableDelete={null}
                        userInfo={userInfo}
                    />
                    {costFundingPopoverOpen && (
                        <CostsFundingPopover
                            open={costFundingPopoverOpen}
                            onClose={onCloseManageCosts}
                            userInfo={userInfo}
                            isTreatment={isTreatment}
                            isActual={false}
                            editableFeature={editableFeature}
                            setBaseFeatureAttributes={
                                isTreatment ? setBaseTreatmentAttributes : setBaseActivityAttributes
                            }
                            setEditableFeature={setEditableFeature}
                            mode={mode}
                            userRoleInfo={userRoleInfo}
                            isApproved={isApproved}
                            rederiveFeatureInfo={rederiveFeatureInfo}
                            setLocationDepartmentValue={setLocationDepartmentValue}
                            setLocationAgencyValue={setLocationAgencyValue}
                            setLocationRegionValue={setLocationRegionValue}
                            setLocationUnitValue={setLocationUnitValue}
                            featureUnsavedFields={
                                isTreatment
                                    ? treatmentValidationUnsavedValues
                                    : activityValidationUnsavedValues
                            }
                        />
                    )}
                </div>
                <div className='incident-list' style={{ height: 'calc(100vh - 200px)' }}>
                    {groupDetails && getListPanel()}
                </div>
            </Drawer>
            {unApprovalEditorOpen && (
                <InFormGenericModal
                    openFlag={unApprovalEditorOpen}
                    closeMethod={closeUnApprovalEditor}
                    titleText={'Request UnApproval'}
                    displayButton={false}
                >
                    <Box
                        style={{
                            maxHeight: '300px',
                            overflow: 'auto',
                            width: '100%',
                            minWidth: '650px',
                        }}
                    >
                        <Grid
                            item
                            size={12}
                            container
                            direction='row'
                            className='grid-item'
                            style={{ alignItems: 'center', paddingLeft: '10px' }}
                        >
                            <Grid
                                item
                                size={12}
                                container
                                direction='row'
                                className='grid-item'
                                style={{ alignItems: 'center' }}
                            >
                                <Grid item size={3}>
                                    <Typography display={'inline'}>UnApproval Date: </Typography>
                                </Grid>
                                <Grid item size={9}>
                                    <FormControl variant='standard' className={classes.formControl}>
                                        <ThemeProvider theme={theme}>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DesktopDatePicker
                                                    format='MM/dd/yyyy'
                                                    fullWidth
                                                    disabled={true}
                                                    {...(unApprovalDate
                                                        ? { InputProps: { classes } }
                                                        : (
                                                                isTreatment
                                                                    ? activityValidationUnsavedValues[
                                                                          FieldNames.featureFields
                                                                              .unApprovalDate
                                                                      ]
                                                                    : treatmentValidationUnsavedValues[
                                                                          FieldNames.featureFields
                                                                              .unApprovalDate
                                                                      ]
                                                            )
                                                          ? {
                                                                InputProps: {
                                                                    classes: notSavedClasses,
                                                                },
                                                            }
                                                          : { InputProps: {} })}
                                                    id={`datePicker-un-approval-date`}
                                                    value={unApprovalDate}
                                                    onChange={(value) => {
                                                        if (value === null) {
                                                            setUnApprovalDate(value);
                                                        } else {
                                                            if (!isNaN(Date.parse(value))) {
                                                                const dateErrString =
                                                                    validateUnapprovalDate(value);
                                                                if (!dateErrString) {
                                                                    setUnApprovalDate(value);
                                                                } else {
                                                                    setUnApprovalDate(null);
                                                                }
                                                            } else {
                                                                setUnApprovalDate(null);
                                                            }
                                                        }
                                                    }}
                                                    inputProps={{ classes: false }} // Sets it so that the field can't be typed into for Completion only
                                                    disableOpenPicker={false}
                                                    slotProps={{
                                                        textField: { variant: 'standard' },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </ThemeProvider>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            size={12}
                            container
                            direction='row'
                            className='grid-item'
                            style={{ alignItems: 'center', paddingLeft: '10px' }}
                        >
                            <Grid
                                item
                                size={12}
                                container
                                direction='row'
                                className='grid-item'
                                style={{ alignItems: 'center' }}
                            >
                                <Grid item size={3}>
                                    <Typography display={'inline'}>UnApproval Reason: </Typography>
                                    <Typography display={'inline'} color={'red'}>
                                        {' '}
                                        *
                                    </Typography>
                                </Grid>
                                <Grid item size={9}>
                                    <FormControl variant='standard' className={classes.formControl}>
                                        <ThemeProvider theme={theme}>
                                            <BasicTextField
                                                label={'UnApproval Reason'}
                                                field={'UnapprovalReason'}
                                                value={unApprovalReason}
                                                setValue={setUnApprovalReason}
                                                variant={'outlined'}
                                                useFullWidth={true}
                                                placeholder={'Enter notes for UnApproval Reason'}
                                                useMultiline={true}
                                                triggerEvent={Events.TriggerFieldChange}
                                            />
                                        </ThemeProvider>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            size={12}
                            container
                            direction='row'
                            className='grid-item'
                            style={{ alignItems: 'center', paddingLeft: '10px' }}
                        >
                            <Box
                                sx={{
                                    marginBottom: '5px',
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginRight: '10px',
                                }}
                            >
                                <Button
                                    variant='outlined'
                                    onClick={() => {
                                        setUnApprovalEditorOpen(false);
                                    }}
                                    aria-label='cancel'
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        onHandleAttributeChanged(
                                            FieldNames.featureFields.unApprovalReason,
                                            unApprovalReason
                                        );
                                        setUnApprovalEditorOpen(false);
                                    }}
                                    disabled={!unApprovalReason}
                                    aria-label='edit'
                                    sx={{ marginLeft: '5px' }}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Grid>
                    </Box>
                </InFormGenericModal>
            )}

            <div style={{ top: 64 }}>
                <InFormAlert
                    openFlag={warningOpen}
                    closeMethod={handleClose}
                    buttonMethod={handleContinue}
                    titleText={warningTitle}
                    contentText={warningContent}
                    buttonText={warningButtonText}
                />
                <ThemeProvider theme={theme}>
                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        className={classes.snackBarStyle}
                        onClose={() => {
                            setSnackbarOpen(false);
                            setSnackbarMessage('');
                        }}
                    >
                        <Alert
                            onClose={() => {
                                setSnackbarOpen(false);
                                setSnackbarMessage('');
                            }}
                            severity={warningSeverity}
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </ThemeProvider>
            </div>
        </Box>
    );
}
